import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import DatePickerField from './UI/DatePickerField';
import FormSelect from './UI/FormSelect';
import LeaveHalfDaySegmentedControl from './Leave/LeaveHalfDaySegmentedControl';
import OverseasTravelDeclarationFields from './Leave/OverseasTravelDeclarationFields';
import LeaveExcludedDaysCallout from './Leave/LeaveExcludedDaysCallout';
import {
  X,
  Calendar,
  CalendarRange,
  Camera,
  Upload,
  AlertCircle,
  AlertTriangle,
  Info,
  Clock,
  FileText,
  Stethoscope,
  Paperclip,
  Smartphone,
  Trash2,
  MapPin,
  Building,
  Users,
  Send,
  Home,
  Heart,
  GraduationCap,
  Loader2
} from 'lucide-react';
import toast from '../utils/toast';
import { staffAPI, configAPI } from '../services/api';
import { formatDate, formatTimeString12Hour, toLocalISODate, parseLocalISOToDate } from '../utils/dateUtils';
import { validateOILDate as validateOILDatePure } from '../utils/oilDateValidator';
import {
  formatOilWorkedHours,
  OIL_FULL_DAY_MIN_HOURS,
  shouldCapOilToHalfDay,
  summarizeOilAttendance,
} from '../utils/oilHoursCap';
import { formatLeaveDays, pluralizeDays } from '../utils/numberUtils';
import {
  parsePrompt,
  buildOverseasTravelPayload,
  clearedOverseasTravelState,
  validateOverseasTravelFields,
  extractOverseasTravelServerErrors,
  hasOverseasTravelFieldErrors,
} from '../utils/overseasTravelDeclaration';
import { 
  isOffInLieuName, 
  isOffInLieuClaimName,
  isOffInLieuUsageName,
  isExternalActivityName,
  isOnCourseName,
  WFH_REASONS, 
  isWFHLeaveType,
  OIL_CLAIM_WINDOW_DAYS,
  OIL_EXPIRY_DAYS,
  validateOILClaimWindow,
  calculateOILExpiryDate,
  isNSLeaveName,
  isNSLeaveType,
  isCompassionateLeaveName,
  getAllCompassionateRelationships,
  getCompassionateEntitlementDays,
} from '../constants/leaveTypes';
import Modal from './UI/Modal';
import ConfirmModal from './UI/ConfirmModal';
import PillToggle from './UI/PillToggle';
import { InfoBadge } from './UI/InfoBadge';
import { useAuth } from '../contexts/AuthContext';
import NSLeaveApplicationModal from './NSLeaveApplicationModal';
import { useSafeDiscardAndClose } from '../hooks/useSafeDiscardAndClose';
import { normalizeHeicFileForUpload } from '../utils/imageUtils';
import { useRestDayStreak } from '../hooks/useRestDayStreak';
import { formatConsumptionProposalText } from '../utils/leaveDisplayUtils';

// Helper to detect annual vs sick leave
const isSickLeaveName = (name = '') => {
  const n = (name || '').toLowerCase();
  return n.includes('outpatient') || n.includes('sick') || n.includes('hospitalisation');
};
const isAnnualLeaveName = (name = '') => {
  const n = (name || '').toLowerCase();
  // Primary patterns
  if (n.includes('annual') || n.includes('vacation')) return true;
  
  // Additional patterns for common annual leave names
  if (n.includes('holiday') && !n.includes('public')) return true;
  if (n.includes('personal') && n.includes('leave')) return true;
  if (n === 'leave' || n === 'casual leave' || n === 'earned leave') return true;
  
  return false;
};

// Helper to detect WFH leave type
const isWFHLeaveName = (name = '') => {
  const n = (name || '').toLowerCase();
  return n.includes('work from home') || n.includes('wfh') || n.includes('remote work') || n.includes('telework');
};

// Normalize leave type naming across varying API shapes
// Attempts multiple common properties and nested shapes
const getLeaveTypeName = (type) => {
  if (!type) return '';
  const candidates = [
    type.leave_type_name,
    type.name,
    type.type_name,
    type.display_name,
    type.leave_type?.leave_type_name,
    type.leave_type?.name,
    type.leave_type?.type_name
  ];
  const value = candidates.find(v => typeof v === 'string' && v.trim().length > 0);
  return (value || '').toString();
};

// Extract a leave type code if present (used for graceful naming fallback)
const getLeaveTypeCode = (type) => {
  if (!type) return '';
  const candidates = [
    type.leave_type_code,
    type.code,
    type.type_code,
    type.leave_type?.code
  ];
  const value = candidates.find(v => typeof v === 'string' && v.trim().length > 0);
  return (value || '').toString();
};

// Known code → display name fallbacks
const codeToDisplayName = {
  S0102: 'Childcare Leave'
};

const SmartLeaveApplicationModal = ({ 
  isOpen, 
  onClose, 
  availableLeaveTypes = [], 
  isLoading = false,
  onSubmit, 
  editingLeave,
  onSwitchToWorkerLeave // optional callback to transition to Worker Leave stepper (Home Leave & Annual Leave)
}) => {
  // FIXED: Only use eligible leave types passed from parent - no fallback to test data
  // This ensures users only see leave types they're actually entitled to
  // Add safety check to ensure it's always an array
  const effectiveLeaveTypes = Array.isArray(availableLeaveTypes) ? availableLeaveTypes : [];

  // Must be declared BEFORE any useMemo that references isWorker (temporal dead zone otherwise → white screen on Leave page)
  const { user } = useAuth();
  const isWorker = user?.employment_classification === 'worker';

  // Sort leave types by frequency of use (UX optimization)
  // Order: Most frequently used at top, rarely used at bottom
  const sortedLeaveTypes = useMemo(() => {
    const staffLeaveTypeOrder = {
      // Most frequently used (daily/weekly)
      'Annual Leave': 1,
      'Outpatient Sick Leave': 2,
      'Hospitalisation Leave': 3,
      'Work From Home': 4,
      // Moderately used (monthly)
      'Childcare Leave': 5,
      'Childcare Leave (Non-Citizen)': 6,
      'Extended Childcare Leave': 7,
      // Occasionally used
      'Claim: Off in Lieu': 8,
      'Off in Lieu': 9,
      'External Activity': 10,
      'On Course': 10.5,
      // Rarely used (life events)
      'Marriage Leave': 11,
      'Paternity Leave': 12,
      'Maternity Leave (Citizen Child)': 13,
      'Maternity Leave (Non-Citizen Child)': 14,
      'Maternity Leave (Unpaid)': 15,
      'Shared Parental Leave': 16,
      'Compassionate Leave': 17,
      'Compassionate Leave (Immediate Family)': 17,
      'Compassionate Leave (Grandparent)': 17,
      'Unpaid Infant Care Leave': 18,
      'Home Leave': 19,
      'National Service Leave': 20,
      // Last resort
      'Unpaid Leave': 99,
    };

    const workerLeaveTypeOrder = {
      // Worker-initiated (personal leave)
      'Annual Leave': 1,
      'Outpatient Sick Leave': 2,
      'Hospitalisation Leave': 3,
      'Home Leave - Unpaid': 4,
      'Urgent Leave - Unpaid': 5,
      'Compassionate Leave': 6,
      'Compassionate Leave (Immediate Family)': 6,
      'Compassionate Leave (Grandparent)': 6,
      // Company-directed (admin assigns, worker doesn't self-initiate)
      'External Activity': 7,
      'On Course': 8,
      'Unpaid Leave': 99,
    };

    const leaveTypeOrder = isWorker ? workerLeaveTypeOrder : staffLeaveTypeOrder;

    return [...effectiveLeaveTypes].sort((a, b) => {
      const nameA = a.leave_type_name || a.name || '';
      const nameB = b.leave_type_name || b.name || '';
      const orderA = leaveTypeOrder[nameA] ?? 50;
      const orderB = leaveTypeOrder[nameB] ?? 50;
      return orderA - orderB;
    });
  }, [effectiveLeaveTypes, isWorker]);

  // Map compassionate leave sub-types to their backend LeaveType objects (Option C: one dropdown, two backend types)
  const compassionateLeaveTypeMap = useMemo(() => {
    const map = {};
    effectiveLeaveTypes.forEach(t => {
      const name = getLeaveTypeName(t);
      if (!isCompassionateLeaveName(name)) return;
      const lower = name.toLowerCase();
      if (lower.includes('grandparent')) map.grandparent = t;
      else if (lower.includes('immediate family')) map.immediate_family = t;
      else if (!map.immediate_family) map.immediate_family = t;
    });
    return map;
  }, [effectiveLeaveTypes]);

  // Deduped leave types for the dropdown — merge both compassionate entries into one "Compassionate Leave"
  // Workers: also exclude WFH (workers are on-site, WFH is not applicable)
  const displayLeaveTypes = useMemo(() => {
    let seenCompassionate = false;
    return sortedLeaveTypes.filter(t => {
      const name = getLeaveTypeName(t);
      if (isWorker && isWFHLeaveName(name)) return false;
      if (isCompassionateLeaveName(name)) {
        if (seenCompassionate) return false;
        seenCompassionate = true;
      }
      return true;
    });
  }, [sortedLeaveTypes, isWorker]);

  // Determine employee work schedule type for OIL validation
  const employeeScheduleType = user?.work_schedule_template?.schedule_type || '5_day';
  const is5HalfDaySchedule = employeeScheduleType === '5_5_day';

  // Fetch public holidays — used by OIL date validation AND the status-only
  // (WFH / External Activity / On Course) date-picker greyout below.
  const { data: publicHolidays = [] } = useQuery({
    queryKey: ['public-holidays-oil'],
    queryFn: async () => {
      try {
        const res = await configAPI.getPublicHolidays();
        return res?.data?.results || res?.data || [];
      } catch (e) {
        console.error('Failed to fetch public holidays:', e);
        return [];
      }
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Working-day filter for status-only leave types (WFH, External Activity, On Course).
  // Uses the employee's work schedule to grey-out rest days in the date picker.
  // Also greys out public holidays — the server rejects these types on a PH
  // with the same "rest day" error, so surfacing it in the picker up front
  // avoids the applicant picking a date only to be told it's invalid after
  // the fact (the red isRestDayBlock banner still covers dates typed/pasted
  // in directly, or picked before publicHolidays finishes loading).
  const scheduleTemplate = user?.work_schedule_template;
  const publicHolidayDateSet = useMemo(
    () => new Set((publicHolidays || []).map((ph) => ph.holiday_date || ph.date).filter(Boolean)),
    [publicHolidays],
  );
  const isWorkingDay = useCallback((date) => {
    if (publicHolidayDateSet.has(toLocalISODate(date))) {
      return false;
    }
    if (!scheduleTemplate) {
      // Fallback: 5-day week (Mon-Fri)
      const day = date.getDay();
      return day !== 0 && day !== 6;
    }
    const dayMap = [
      scheduleTemplate.sunday_enabled,
      scheduleTemplate.monday_enabled,
      scheduleTemplate.tuesday_enabled,
      scheduleTemplate.wednesday_enabled,
      scheduleTemplate.thursday_enabled,
      scheduleTemplate.friday_enabled,
      scheduleTemplate.saturday_enabled,
    ];
    return !!dayMap[date.getDay()];
  }, [scheduleTemplate, publicHolidayDateSet]);
  
  // Debug logging moved to after variable declarations
  const [formData, setFormData] = useState({
    leave_type_id: '',
    leave_reason: '',
    leave_reason_details: '', // For custom reason when "Other" is selected
    leave_medical_cert_file: null,
    leave_start_date: '',
    leave_end_date: '',
    leave_start_time_period: 'AM', // New field for AM/PM
    leave_end_time_period: 'PM',   // New field for AM/PM
    // NEW: Off in Lieu specific fields
    saturday_work_date: '', // Keep for backwards compatibility, will sync with oil_dates[0]
    work_description: '',
    project_code: '',
    project_name: '', // Store project name for better data completeness
    oil_days: '', // Keep for backwards compatibility, will be calculated from oil_dates
    // Multi-date OIL support
    oil_dates: [], // Array of { date: 'YYYY-MM-DD', days: 0.5 | 1 }
    // NEW: External Activity specific fields
    activity_type: '',
    other_activity_type: '', // For custom activity type when "Other" is selected
    activity_location: '',
    business_justification: '',
    // NEW: WFH specific fields
    wfh_reason: '',
    wfh_other_reason: '',
    // On Course specific fields
    course_name: '',
    course_location: '',
    // Compassionate Leave specific fields
    compassionate_sub_type: '', // 'immediate_family' or 'grandparent'
    compassionate_relationship: '',
    deceased_name: '',
    overseas_travel_declared: null,
    overseas_departure_date: '',
    overseas_return_date: '',
  });

  // All other state variables
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const { isDiscarding, safeDiscardAndClose } = useSafeDiscardAndClose();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mcPreview, setMcPreview] = useState(null);
  const [draftApplicationId, setDraftApplicationId] = useState(null);
  const [ocrStatus, setOcrStatus] = useState({ loading: false, data: null, error: null });
  const [calculatedLeaveDays, setCalculatedLeaveDays] = useState(0);
  const [excludedLeaveDates, setExcludedLeaveDates] = useState([]);
  const [overseasTravelPrompt, setOverseasTravelPrompt] = useState({
    should_prompt: false,
    opportunity_start: null,
    opportunity_end: null,
    opportunity_calendar_days: 0,
  });
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcFailed, setCalcFailed] = useState(false);
  const overseasTravelSectionRef = useRef(null);
  const [proposalDisplay, setProposalDisplay] = useState('');
  const [futureYearProjection, setFutureYearProjection] = useState(null);
  const [yearEndPriority, setYearEndPriority] = useState(null);
  const [balanceWarning, setBalanceWarning] = useState(null); // Warning when leave exceeds available balance
  const [oilAttendanceByDate, setOilAttendanceByDate] = useState({});
  const [conflictCheck, setConflictCheck] = useState({ conflicts: [], hasConflicts: false, dateOrderError: false });
  // MOM Compliance: Track sick leave validation (rest days, public holidays, overlapping leave)
  const [momValidation, setMomValidation] = useState({ errors: [], warnings: [], blockedDates: [], hasMomViolations: false, workingDays: 0 });
  // MOM Compliance: PH Forfeiture (Before & After / Sandwich Rule) for Unpaid Leave
  const [phForfeiture, setPhForfeiture] = useState({ wouldForfeit: false, forfeitedHolidays: [], forfeitedDays: 0, warningMessage: '' });
  
  // Compassionate Leave: Death certificate upload state
  const [deathCertDocId, setDeathCertDocId] = useState(null);
  const [deathCertUploading, setDeathCertUploading] = useState(false);
  const [deathCertFileName, setDeathCertFileName] = useState(null);
  const deathCertInputRef = useRef(null);
  
  // Project search state
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  // Prefill when editing a draft
  useEffect(() => {
    if (!editingLeave) return;
    // Resolve leave type id from multiple shapes
    let ltId = editingLeave.leave_type_id?.id || editingLeave.leave_type_id || editingLeave.leave_type?.id || '';
    if (!ltId) {
      const name = editingLeave.leave_type?.leave_type_name || editingLeave.leave_type_id?.leave_type_name || '';
      if (name) {
        const match = effectiveLeaveTypes.find(t => (t.leave_type_name || '').toLowerCase() === name.toLowerCase());
        if (match) ltId = match.id;
      }
    }
    setFormData(prev => ({
      ...prev,
      leave_type_id: ltId ? String(ltId) : prev.leave_type_id,
      leave_reason: editingLeave.leave_reason || '',
      leave_reason_details: editingLeave.leave_reason_details || '',
      leave_start_date: editingLeave.leave_start_date || '',
      leave_end_date: editingLeave.leave_end_date || editingLeave.leave_start_date || '',
      leave_start_time_period: editingLeave.leave_start_time_period || (editingLeave.leave_half_day_period === 'afternoon' ? 'PM' : 'AM'),
      leave_end_time_period: editingLeave.leave_end_time_period || (editingLeave.leave_half_day_period === 'morning' ? 'AM' : 'PM'),
      // Off in Lieu fields
      saturday_work_date: editingLeave.saturday_work_date || '',
      work_description: editingLeave.work_description || '',
      project_code: editingLeave.project_code || '',
      project_name: editingLeave.project_name || '',
      oil_days: editingLeave.oil_days || '',
      // Multi-date OIL support - convert legacy single date to array format
      oil_dates: editingLeave.oil_dates || (editingLeave.saturday_work_date ? [{ date: editingLeave.saturday_work_date, days: editingLeave.oil_days || 0.5 }] : []),
      // External Activity fields
      activity_type: editingLeave.activity_type || '',
      other_activity_type: editingLeave.other_activity_type || '',
              activity_location: editingLeave.activity_location || '',
      business_justification: editingLeave.business_justification || '',
      // On Course fields
      course_name: editingLeave.course_name || '',
      course_location: editingLeave.course_location || '',
      overseas_travel_declared:
        editingLeave.overseas_travel_declared === true
          ? true
          : editingLeave.overseas_travel_declared === false
            ? false
            : null,
      overseas_departure_date: editingLeave.overseas_departure_date || '',
      overseas_return_date: editingLeave.overseas_return_date || '',
    }));
  }, [editingLeave, effectiveLeaveTypes]);

  // Load active projects for project selector (code + name)
  const { data: activeProjects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: async () => {
      try {
        const res = await staffAPI.getActiveProjects();
        return res.data?.results || res.data || [];
      } catch (e) {
        // Fallback to generic list if "active" endpoint not available
        const res = await staffAPI.getProjects?.({ status: 'active', page_size: 500 }).catch(() => ({ data: [] }));
        return res?.data?.results || res?.data || [];
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const validProjectCodes = new Set((activeProjects || []).map(p => String(p.project_code || '')));

  // OIL date validation helper.
  // Pure logic lives in `frontend/src/utils/oilDateValidator.js` so the
  // truth table can be unit-tested without rendering the modal. See
  // .cursor/rules/oil-weekday-after-hours-non-workman-only.mdc.
  const validateOILDate = useCallback(
    (selectedDate) =>
      validateOILDatePure(selectedDate, {
        publicHolidays,
        is5HalfDaySchedule,
        employeeScheduleType,
        isWorker,
      }),
    [publicHolidays, is5HalfDaySchedule, employeeScheduleType, isWorker],
  );

  // Current OIL date validation state (for backwards compatibility with single date)
  const oilDateValidation = useMemo(() => {
    return validateOILDate(formData.saturday_work_date);
  }, [formData.saturday_work_date, validateOILDate]);

  // Multi-date OIL helpers
  const addOILDate = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      oil_dates: [...prev.oil_dates, { date: '', days: '' }]
    }));
  }, []);

  const removeOILDate = useCallback((index) => {
    setFormData(prev => {
      const newDates = prev.oil_dates.filter((_, i) => i !== index);
      // Sync saturday_work_date and oil_days with first entry for backwards compatibility
      const firstEntry = newDates[0];
      return {
        ...prev,
        oil_dates: newDates,
        saturday_work_date: firstEntry?.date || '',
        oil_days: firstEntry?.days || ''
      };
    });
  }, []);

  const updateOILDate = useCallback((index, field, value) => {
    setFormData(prev => {
      const newDates = [...prev.oil_dates];
      newDates[index] = { ...newDates[index], [field]: value };
      // If changing date, check validation and enforce forceFullDay if needed
      if (field === 'date') {
        const dateValidation = validateOILDate(value);
        // MOM Compliance: Auto-set to 1.0 day for Saturday PH (5-day employees)
        if (dateValidation.forceFullDay) {
          newDates[index].days = 1;
        } else {
          newDates[index].days = '';
        }
      }
      // Sync saturday_work_date and oil_days with first entry for backwards compatibility
      const firstEntry = newDates[0];
      return {
        ...prev,
        oil_dates: newDates,
        saturday_work_date: firstEntry?.date || '',
        oil_days: firstEntry?.days || ''
      };
    });
  }, [validateOILDate]);

  // Calculate total OIL days from all dates
  const totalOILDays = useMemo(() => {
    return formData.oil_dates.reduce((sum, entry) => {
      const days = parseFloat(entry.days) || 0;
      return sum + days;
    }, 0);
  }, [formData.oil_dates]);

  // Validate all OIL dates
  const allOILDatesValid = useMemo(() => {
    if (formData.oil_dates.length === 0) return false;
    return formData.oil_dates.every(entry => {
      if (!entry.date || !entry.days) return false;
      const validation = validateOILDate(entry.date);
      return validation.valid;
    });
  }, [formData.oil_dates, validateOILDate]);

  // Check if a date is already selected (to prevent duplicates)
  const isDateAlreadySelected = useCallback((dateStr, currentIndex) => {
    return formData.oil_dates.some((entry, i) => i !== currentIndex && entry.date === dateStr);
  }, [formData.oil_dates]);
  
  // Initialize filtered projects when activeProjects loads
  useEffect(() => {
    if (activeProjects && activeProjects.length > 0) {
      setFilteredProjects(activeProjects);
    }
  }, [activeProjects]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProjectDropdown && !event.target.closest('.project-dropdown-container')) {
        setShowProjectDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProjectDropdown]);

  const annualLeaveReasons = useMemo(() => {
    if (isWorker) {
      return [
        'Home Leave',
        'Personal Matter',
        'Training / Course / Exam',
        'Medical Appointment',
        'Administrative (Passport / Banking)',
        'Other',
      ];
    }
    return [
      'Vacation',
      'Medical Appointment',
      'Personal Matter',
      'Training / Course',
      'Religious Holiday',
      'Emergency',
      'Other',
    ];
  }, [isWorker]);

  // Predefined activity types for External Activity
  const activityTypes = [
    'Site Walk/Survey/Inspection',
    'Client/Consultant/Supplier/Coordination Meeting',
    'Conference/Seminar/Workshop/Training',
    'Tender Meeting/Presentation',
    'Factory Acceptance Test (FAT)',
    'Safety Induction Course (SIC)',
    'Service/Maintenance/Repair Work',
    'Company Event (CSR/Team Bonding)',
    'Trade Show/Exhibition',
    'Business Development',
    'Subsidiary/Group Company Meeting',
    'Networking Event',
    'Government/Authority Meeting',
    'Career Fair/Educational Institution',
    'Award Ceremony/Gala',
    'Annual Health Screening',
    'Other'
  ];

  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Attendance verification for Off in Lieu — drives the 4-hour / 0.5-day cap.
  const verifyOilAttendance = async (workDate) => {
    if (!workDate) return;

    setOilAttendanceByDate((prev) => ({
      ...prev,
      [workDate]: { ...(prev[workDate] || {}), loading: true },
    }));

    try {
      const response = await staffAPI.getMyEvents({ start_date: workDate, end_date: workDate });
      const events = Array.isArray(response?.data) ? response.data : [];
      const summary = summarizeOilAttendance(events);

      setOilAttendanceByDate((prev) => ({
        ...prev,
        [workDate]: { loading: false, ...summary },
      }));

      if (summary.hasPair && shouldCapOilToHalfDay(summary)) {
        toast.info(`Clocked ${formatOilWorkedHours(summary.hours)} — this date earns 0.5 day.`);
      } else if (summary.hasPair) {
        toast.success('Attendance verified for this date');
      } else if (!summary.hasClockIn && !summary.hasClockOut) {
        toast.info('No attendance records found. You can still submit — your RO will review.');
      }
    } catch (error) {
      console.error('Attendance verification error:', error);
      setOilAttendanceByDate((prev) => ({
        ...prev,
        [workDate]: { loading: false, hasPair: false, hours: null },
      }));
    }
  };

  // Check for leave conflicts when dates change - silent background check
  const checkLeaveConflicts = async (startDate, endDate) => {
    // Validate date order first — do NOT conflate with leave conflicts
    if (!startDate || !endDate) {
      setConflictCheck({ conflicts: [], hasConflicts: false, dateOrderError: false });
      setMomValidation({ errors: [], warnings: [], blockedDates: [], hasMomViolations: false, workingDays: 0 });
      return;
    }

    // Parse as local calendar dates (YYYY-MM-DD) to avoid UTC off-by-one
    const start = parseLocalISOToDate(startDate);
    const end = parseLocalISOToDate(endDate);
    
    // Check if dates are valid
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      setConflictCheck({
        conflicts: [],
        hasConflicts: false,
        dateOrderError: true
      });
      setMomValidation({ errors: [], warnings: [], blockedDates: [], hasMomViolations: false, workingDays: 0 });
      setPhForfeiture({ wouldForfeit: false, forfeitedHolidays: [], forfeitedDays: 0, warningMessage: '' });
      return;
    }

    // Allow same dates (start date can equal end date)
    if (start > end) {
      setConflictCheck({
        conflicts: [],
        hasConflicts: false,
        dateOrderError: true
      });
      setMomValidation({ errors: [], warnings: [], blockedDates: [], hasMomViolations: false, workingDays: 0 });
      setPhForfeiture({ wouldForfeit: false, forfeitedHolidays: [], forfeitedDays: 0, warningMessage: '' });
      return;
    }

    // Silent background check - no loading state to prevent glitch-like behavior
    try {
      const response = await staffAPI.checkLeaveConflicts({
        start_date: startDate,
        end_date: endDate,
        exclude_application_id: editingLeave?.id,
        leave_type_id: formData.leave_type_id || null,  // MOM COMPLIANCE: Include leave type for sick leave validation
        start_time_period: formData.leave_start_time_period || 'AM',
        end_time_period: formData.leave_end_time_period || 'PM',
      });
      
      setConflictCheck({
        conflicts: response.data.conflicts || [],
        hasConflicts: response.data.has_conflicts || false,
        dateOrderError: false
      });
      
      // MOM COMPLIANCE: For sick leave, also check MOM validation
      // This includes rest days, public holidays, and overlapping Annual Leave/Unpaid Leave
      if (response.data.mom_validation) {
        setMomValidation({
          errors: response.data.mom_validation.errors || [],
          warnings: response.data.mom_validation.warnings || [],
          blockedDates: response.data.mom_validation.blocked_dates || [],
          hasMomViolations: response.data.mom_validation.has_mom_violations || false,
          workingDays: response.data.mom_validation.working_days || 0
        });
      } else {
        setMomValidation({ errors: [], warnings: [], blockedDates: [], hasMomViolations: false, workingDays: 0 });
      }
      
      // MOM COMPLIANCE: PH Forfeiture (Before & After / Sandwich Rule)
      // For Unpaid Leave, check if proposed dates would cause PH pay forfeiture
      if (response.data.ph_forfeiture) {
        setPhForfeiture({
          wouldForfeit: response.data.ph_forfeiture.would_forfeit || false,
          forfeitedHolidays: response.data.ph_forfeiture.forfeited_holidays || [],
          forfeitedDays: response.data.ph_forfeiture.forfeited_days || 0,
          warningMessage: response.data.ph_forfeiture.warning_message || '',
        });
      } else {
        setPhForfeiture({ wouldForfeit: false, forfeitedHolidays: [], forfeitedDays: 0, warningMessage: '' });
      }
      
    } catch (error) {
      console.error('Error checking leave conflicts:', error);
      // Silent error handling - don't show loading state or error to user
      setConflictCheck({ conflicts: [], hasConflicts: false, dateOrderError: false });
      setMomValidation({ errors: [], warnings: [], blockedDates: [], hasMomViolations: false, workingDays: 0 });
      setPhForfeiture({ wouldForfeit: false, forfeitedHolidays: [], forfeitedDays: 0, warningMessage: '' });
    }
  };

  // Helper function to format dates for input fields
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Handle various date formats
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // Try parsing legacy hyphenated DD-MMM-YYYY (like "24-Jun-2025") from OCR/imports
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthIndex = months.findIndex(m => m === parts[1]);
          if (monthIndex !== -1) {
            const newDate = new Date(parseInt(parts[2]), monthIndex, parseInt(parts[0]));
            if (!isNaN(newDate.getTime())) {
              return toLocalISODate(newDate);
            }
          }
          
          // Try parsing DD-MM-YYYY format (like "27-11-2025") from OCR webhook
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
            const newDate = new Date(year, month - 1, day);
            if (!isNaN(newDate.getTime())) {
              return toLocalISODate(newDate);
            }
          }
        }
        return '';
      }
      return toLocalISODate(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Check conflicts when dates or leave type change
  // MOM COMPLIANCE: Re-check when leave type changes (for sick leave MOM validation)
  useEffect(() => {
    if (formData.leave_start_date && formData.leave_end_date) {
      const debounceTimer = setTimeout(() => {
        checkLeaveConflicts(formData.leave_start_date, formData.leave_end_date);
      }, 500); // 500ms debounce
      
      return () => clearTimeout(debounceTimer);
    } else {
      setConflictCheck({ conflicts: [], hasConflicts: false, dateOrderError: false });
      setMomValidation({ errors: [], warnings: [], blockedDates: [], hasMomViolations: false, workingDays: 0 });
      setPhForfeiture({ wouldForfeit: false, forfeitedHolidays: [], forfeitedDays: 0, warningMessage: '' });
    }
  }, [formData.leave_start_date, formData.leave_end_date, formData.leave_type_id, formData.leave_start_time_period, formData.leave_end_time_period, editingLeave?.id]);

  // Server-driven leave days calculation
  useEffect(() => {
    let cancelled = false;
    const runCalc = async () => {
      try {
        if (!formData.leave_start_date || !formData.leave_end_date || !formData.leave_type_id) {
          setCalculatedLeaveDays(0);
          setExcludedLeaveDates([]);
          setProposalDisplay('');
          setFutureYearProjection(null);
          setYearEndPriority(null);
          setBalanceWarning(null);
          setOverseasTravelPrompt(parsePrompt(null));
          setFormData((prev) => clearedOverseasTravelState(prev));
          setCalcFailed(false);
          return;
        }
        // Skip calc when range is invalid — 0 days would falsely trigger rest-day UI
        const start = parseLocalISOToDate(formData.leave_start_date);
        const end = parseLocalISOToDate(formData.leave_end_date);
        const sameDayInvertedHalf =
          formData.leave_start_date === formData.leave_end_date &&
          formData.leave_start_time_period === 'PM' &&
          formData.leave_end_time_period === 'AM';
        if (
          !start ||
          !end ||
          isNaN(start.getTime()) ||
          isNaN(end.getTime()) ||
          start > end ||
          sameDayInvertedHalf
        ) {
          setCalculatedLeaveDays(0);
          setExcludedLeaveDates([]);
          setProposalDisplay('');
          setFutureYearProjection(null);
          setYearEndPriority(null);
          setBalanceWarning(null);
          setOverseasTravelPrompt(parsePrompt(null));
          setFormData((prev) => clearedOverseasTravelState(prev));
          setCalcFailed(false);
          return;
        }
        const selType = effectiveLeaveTypes.find(t => String(t.id) === String(formData.leave_type_id));
        if (!selType) {
          setCalculatedLeaveDays(0);
          setExcludedLeaveDates([]);
          setProposalDisplay('');
          setFutureYearProjection(null);
          setYearEndPriority(null);
          setBalanceWarning(null);
          setOverseasTravelPrompt(parsePrompt(null));
          setFormData((prev) => clearedOverseasTravelState(prev));
          setCalcFailed(false);
          return;
        }
        setCalcFailed(false);
        setCalcLoading(true);
        setProposalDisplay('');
        setFutureYearProjection(null);
        setYearEndPriority(null);
        setBalanceWarning(null);
        const payload = {
          start_date: formData.leave_start_date,
          end_date: formData.leave_end_date,
          leave_type_name: selType.leave_type_name,
        };
        const resp = await staffAPI.calculateLeaveDays(payload);
        if (cancelled) return;
        let days = resp?.data?.calculation?.mom_compliant_days ?? 0;
        const startIsPhysicalHalf = resp?.data?.calculation?.start_is_physical_half_day;
        const endIsPhysicalHalf = resp?.data?.calculation?.end_is_physical_half_day;

        // Apply client half-day adjustments only for non-sick leave if user picks AM/PM on edges.
        // Skip the adjustment on physical half-day boundaries (e.g. Saturday on a 5.5-day
        // schedule) because the entire scheduled shift IS the single session — "AM" on
        // such a day means the full Saturday, not half of it.
        if (!isSickLeaveName(selType.leave_type_name)) {
          if (formData.leave_start_time_period === 'PM' && !startIsPhysicalHalf) days -= 0.5;
          if (formData.leave_end_time_period === 'AM' && !endIsPhysicalHalf) days -= 0.5;
        }

        const numericDays = Math.max(0, Number(days));
        setCalculatedLeaveDays(numericDays);
        setExcludedLeaveDates(resp?.data?.calculation?.excluded_dates || []);

        const prompt = parsePrompt(
          resp?.data?.overseas_travel_prompt || resp?.data?.calculation?.overseas_travel_prompt,
        );
        setOverseasTravelPrompt(prompt);
        if (!prompt.should_prompt) {
          setFormData((prev) => clearedOverseasTravelState(prev));
        }

        // Fetch OIL→AL proposal for Annual Leave to display split in badge
        const selectedNameInner = getLeaveTypeName(selType);
        const isAnnualInner = isAnnualLeaveName(selectedNameInner);
        if (isAnnualInner && numericDays > 0 && formData.leave_start_date && formData.leave_end_date) {
          try {
            const resp2 = await staffAPI.proposeConsumption({
              start_date: formData.leave_start_date,
              end_date: formData.leave_end_date,
              total_days: numericDays,
              // Must match calculateLeaveDays AM/PM trim or the walk prices 1.0 day
              // while total_days is 0.5 → bogus "(0.5 OIL + 0.5 AL)" for a half-day.
              start_time_period: formData.leave_start_time_period || undefined,
              end_time_period: formData.leave_end_time_period || undefined,
            });
            if (cancelled) return;
            const proposal = resp2?.data?.proposal || {};
            const items = proposal.items || [];
            const projection = proposal.future_year_projection || null;
            setFutureYearProjection(projection);
            setYearEndPriority(proposal.year_end_priority || null);
            if (
              isAnnualLeaveName(selType.leave_type_name)
              && Math.max(start.getFullYear(), end.getFullYear()) > new Date().getFullYear()
              && !projection
            ) {
              setCalcFailed(true);
            }
            const visibleItems = items.filter(
              (item) => Number(item?.days || 0) > 0
            );
            setProposalDisplay(
              visibleItems.length > 1
                ? formatConsumptionProposalText(visibleItems)
                : ''
            );
            const unpaidDays = visibleItems
              .filter((item) => /unpaid/i.test(item.leave_type || ''))
              .reduce(
                (sum, item) => sum + Number(item.days || 0),
                0
              );
            const proposalTotal = Number(proposal.total_days);
            if (Number.isFinite(proposalTotal) && proposalTotal > 0) {
              setCalculatedLeaveDays(proposalTotal);
            }
            
            // Check if unpaid leave is being used (informational notice, not an error)
            // Since UL is now in the priority list, unallocated_days should always be 0
            // We show a notice when UL is part of the consumption
            if (unpaidDays > 0) {
              setBalanceWarning({
                type: 'unpaid_notice',
                unpaidDays,
                requested: proposalTotal || numericDays
              });
            } else {
              setBalanceWarning(null);
            }
          } catch (e) {
            setProposalDisplay('');
            setFutureYearProjection(null);
            setYearEndPriority(null);
            setBalanceWarning(null);
            if (isAnnualLeaveName(selType.leave_type_name)) {
              setCalcFailed(true);
            }
          }
        } else {
          setProposalDisplay('');
          setFutureYearProjection(null);
          setYearEndPriority(null);
          setBalanceWarning(null);
        }
      } catch (e) {
        if (cancelled) return;
        // Fallback: keep previous or zero. Do not silently hide a required
        // overseas-travel question — submit stays blocked until calc succeeds.
        setCalculatedLeaveDays(0);
        setExcludedLeaveDates([]);
        setProposalDisplay('');
        setFutureYearProjection(null);
        setYearEndPriority(null);
        setBalanceWarning(null);
        setOverseasTravelPrompt(parsePrompt(null));
        setFormData((prev) => clearedOverseasTravelState(prev));
        setCalcFailed(true);
      } finally {
        if (!cancelled) setCalcLoading(false);
      }
    };

    runCalc();
    return () => {
      cancelled = true;
    };
  }, [formData.leave_start_date, formData.leave_end_date, formData.leave_start_time_period, formData.leave_end_time_period, formData.leave_type_id, effectiveLeaveTypes]);

  // Dynamic field configuration based on leave type
  const selectedLeaveType = effectiveLeaveTypes.find(t => String(t.id) === String(formData.leave_type_id));
  const selectedName = getLeaveTypeName(selectedLeaveType);
  const lowerName = selectedName.toLowerCase();
  
  const isSick = isSickLeaveName(selectedName);
  const isAnnual = isAnnualLeaveName(selectedName);
  const annualLeaveStartsInProjectedYear = Boolean(
    isAnnual
    && futureYearProjection?.supported
    && Number(String(formData.leave_start_date || '').slice(0, 4))
      === Number(futureYearProjection.balance_year)
  );
  const showProjectedAnnualCard = Boolean(
    isAnnual
    && futureYearProjection?.supported
    && (
      annualLeaveStartsInProjectedYear
      || Number(futureYearProjection.annual_leave_days_in_request || 0) > 0
    )
  );
  const isOffInLieu = isOffInLieuName(selectedName);  // Any OIL type
  const isOffInLieuClaim = isOffInLieuClaimName(selectedName);  // "Claim: Off in Lieu" (accrual)
  const isOffInLieuUsage = isOffInLieuUsageName(selectedName);  // "Off in Lieu" (usage/deduction)
  const isExternalActivity = isExternalActivityName(selectedName);
  const isOnCourse = isOnCourseName(selectedName);
  const isWFH = isWFHLeaveName(selectedName);
  const isNSLeave = isNSLeaveName(selectedName) || (selectedLeaveType && isNSLeaveType(selectedLeaveType.id));
  const isCompassionateLeave = isCompassionateLeaveName(selectedName);

  // Status-only types: single-day "I'm working from elsewhere" — invalid on rest days
  const isStatusOnlyType = isWFH || isExternalActivity || isOnCourse;
  // Synchronous date/period-order check (do not wait on debounced conflictCheck —
  // avoids false rest-day flash). Same-day PM→AM is chronologically empty:
  // half-day math yields 0 days and must NOT be treated as a rest day.
  const startDateParsed = formData.leave_start_date ? parseLocalISOToDate(formData.leave_start_date) : null;
  const endDateParsed = formData.leave_end_date ? parseLocalISOToDate(formData.leave_end_date) : null;
  const isPeriodOrderInvalid = !!(
    formData.leave_start_date &&
    formData.leave_end_date &&
    formData.leave_start_date === formData.leave_end_date &&
    formData.leave_start_time_period === 'PM' &&
    formData.leave_end_time_period === 'AM'
  );
  const isCalendarOrderInvalid = !!(
    formData.leave_start_date &&
    formData.leave_end_date &&
    (!startDateParsed ||
      !endDateParsed ||
      isNaN(startDateParsed.getTime()) ||
      isNaN(endDateParsed.getTime()) ||
      startDateParsed > endDateParsed)
  );
  const isDateOrderInvalid = isCalendarOrderInvalid || isPeriodOrderInvalid;
  const isRestDayBlock =
    isStatusOnlyType &&
    calculatedLeaveDays === 0 &&
    !!formData.leave_start_date &&
    !!formData.leave_end_date &&
    !isDateOrderInvalid &&
    !calcLoading;
  const leaveCalcBlocking = Boolean(
    formData.leave_start_date &&
    formData.leave_end_date &&
    formData.leave_type_id &&
    !isDateOrderInvalid &&
    (calcLoading || calcFailed)
  );

  // MOM s36 rest-day streak awareness: WFH / External Activity count as
  // WORKED days toward the 12-day consecutive-working-day ceiling (see
  // RestDayComplianceService.WORK_LEAVE_TYPES). Advisory only — never
  // blocks submission; the applicant just gets to see it before they apply.
  const isWorkCountingType = isWFH || isExternalActivity;
  const { data: restDayStreak } = useRestDayStreak({
    proposedStart: formData.leave_start_date || undefined,
    proposedEnd: formData.leave_end_date || undefined,
    enabled: isWorkCountingType && !isDateOrderInvalid,
  });
  const showStreakAdvisory = isWorkCountingType && !!restDayStreak?.has_warning && !isRestDayBlock;

  // Treat these as document-driven types where a textual reason is not required
  const isDocDrivenNoReason = (
    lowerName.includes('national service') ||
    lowerName.includes('ns') ||
    lowerName.includes('maternity') ||
    lowerName.includes('paternity') ||
    lowerName.includes('childcare') ||
    lowerName.includes('adoption') ||
    lowerName.includes('infant') ||
    isCompassionateLeave ||
    isOffInLieu ||
    isExternalActivity ||
    isOnCourse
  );

  const showMcUpload = selectedLeaveType ? isSickLeaveName(selectedName) : false;
  // For sick/hospitalisation leave we hide date fields (extracted from MC)
  // For "Claim: Off in Lieu" we hide standard dates (use work date picker instead)
  // For "Off in Lieu" (usage), we show standard date pickers (for when to take time off)
  const showDates = !isSick && !isOffInLieuClaim;
  // Only show textual reason for non-sick, non-document-driven types
  const showReason = !!selectedLeaveType && !isSick && !isDocDrivenNoReason && !showMcUpload;

  // Build required fields list
  const requiredFields = ['leave_type_id'];
  if (showMcUpload) requiredFields.push('leave_medical_cert_file');
  if (showDates) {
    requiredFields.push('leave_start_date', 'leave_end_date');
  }
  if (showReason) {
    if (isWFH) {
      requiredFields.push('wfh_reason');
      if (formData.wfh_reason === 'others') {
        requiredFields.push('wfh_other_reason');
      }
    } else {
      requiredFields.push('leave_reason');
      if (isAnnual && formData.leave_reason === 'Other') {
        requiredFields.push('leave_reason_details');
      }
    }
  }
  
  // Add required fields for "Claim: Off in Lieu" (accrual)
  if (isOffInLieuClaim) {
    requiredFields.push('work_description', 'project_code');
    // oil_dates validation is handled separately (must have at least one valid date with days selected)
  }
  
  // "Off in Lieu" (usage) uses standard date fields - no special required fields
  
  // Add required fields for External Activity
  if (isExternalActivity) {
    requiredFields.push('activity_type', 'activity_location', 'business_justification', 'project_code');
    
    // Add other_activity_type as required when "Other" is selected
    if (formData.activity_type === 'Other') {
      requiredFields.push('other_activity_type');
    }
  }
  
  // Add required fields for On Course
  if (isOnCourse) {
    requiredFields.push('course_name', 'course_location');
  }

  const fieldConfig = {
    showMcUpload,
    showDates,
    // Show reason section only when a leave type is selected AND it's Annual Leave, WFH, or other leave types that need reason selection
    showReason: !!selectedLeaveType && (isAnnual || isWFH || (!isSick && !isDocDrivenNoReason && !showMcUpload)),
    requiredFields,
    helpText: selectedLeaveType ? `Selected: ${selectedName}` : ''
  };

  // OIL Claim: verify attendance per work date and keep the leave window in sync
  useEffect(() => {
    if (!isOffInLieuClaim) return;
    const claimDates = formData.oil_dates.map((entry) => entry.date).filter(Boolean);
    const claimDate = formData.saturday_work_date || formData.leave_start_date || claimDates[0];
    claimDates.forEach((workDate) => {
      if (!oilAttendanceByDate[workDate]) {
        verifyOilAttendance(workDate);
      }
    });
    if (claimDate) {
      setFormData((prev) => {
        const needsSync = prev.leave_start_date !== claimDate || prev.leave_end_date !== claimDate;
        return needsSync
          ? { ...prev, leave_start_date: claimDate, leave_end_date: claimDate, leave_start_time_period: 'AM', leave_end_time_period: 'PM' }
          : prev;
      });
    }
  }, [isOffInLieuClaim, formData.saturday_work_date, formData.leave_start_date, formData.oil_dates]);

  // Auto-select 0.5 day when verified hours are under 4 (or Saturday half-day).
  useEffect(() => {
    if (!isOffInLieuClaim) return;
    setFormData((prev) => {
      let changed = false;
      const nextDates = prev.oil_dates.map((entry) => {
        if (!entry.date) return entry;
        const validation = validateOILDate(entry.date);
        const attendance = oilAttendanceByDate[entry.date];
        const capHalf = shouldCapOilToHalfDay({
          hours: attendance?.hours,
          hasPair: attendance?.hasPair,
          forceFullDay: validation.forceFullDay,
          restrict05: validation.restrict05,
        });
        if ((capHalf || validation.restrict05) && Number(entry.days) !== 0.5) {
          changed = true;
          return { ...entry, days: 0.5 };
        }
        return entry;
      });
      if (!changed) return prev;
      return {
        ...prev,
        oil_dates: nextDates,
        oil_days: nextDates[0]?.days || prev.oil_days,
      };
    });
  }, [isOffInLieuClaim, oilAttendanceByDate, validateOILDate]);

  // Handle file upload with camera support
  const handleFileUpload = async (rawFile, source = 'file') => {
    if (!rawFile) return;

    // Convert HEIC/HEIF → JPEG before validation so the type check passes
    const file = await normalizeHeicFileForUpload(rawFile);

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF file');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      // Create file preview
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setMcPreview(e.target.result);
        reader.readAsDataURL(file);
      }

      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Immediately send to n8n for OCR processing (on upload).
      // The file is attached to the form only after the server accepts it,
      // so a duplicate never shows the green "uploaded" tick.
      let applicationId = (editingLeave && editingLeave.id) ? editingLeave.id : draftApplicationId;
      try {
        if (!applicationId) {
          // Ensure we have a leave_type_id to create a draft
          const ltId = formData.leave_type_id;
          if (!ltId) {
            toast.error('Please select a leave type first');
            return;
          }
          const todayStr = toLocalISODate(new Date());
          const draftPayload = {
            leave_type_id: ltId,
            leave_status: 'draft',
            leave_start_date: todayStr,
            leave_end_date: todayStr,
            leave_reason: 'Draft',
            leave_half_day: false,
          };
          try {
            const resp = await staffAPI.createLeaveApplicationDraft(draftPayload);
            applicationId = (
              resp?.data?.id ||
              resp?.data?.pk ||
              resp?.data?.application_id ||
              resp?.data?.result?.id ||
              resp?.data?.data?.id ||
              resp?.data?.data?.pk ||
              null
            );
            if (applicationId) {
              setDraftApplicationId(applicationId);
            }
          } catch (e) {
            console.error('Failed to create draft before OCR:', e);
            toast.error('Failed to create draft for OCR');
            return;
          }
        }
        // Start OCR loading — inline blue card handles the visual feedback
        setOcrStatus({ loading: true, data: null, error: null });

        const resp = await staffAPI.sendMedicalCertToN8N(applicationId, file);

        // Prefer immediate OCR results from n8n response
        const payload = resp?.data || {};
        if (payload.resumed && payload.application_id && String(payload.application_id) !== String(applicationId)) {
          try {
            await staffAPI.discardMcDraft(applicationId, { mode: 'self_service' });
          } catch (discardErr) {
            console.warn('Failed to discard replaced MC draft:', discardErr);
          }
          applicationId = payload.application_id;
          setDraftApplicationId(applicationId);
        }
        setFormData(prev => ({
          ...prev,
          leave_medical_cert_file: file
        }));
        if (payload.resumed) {
          toast.info('This certificate is already on your unfinished application. Review it and submit.');
        }
        const ocr = payload.ocr_data || payload.extracted_data || null;
        const calcDays = (startStr, endStr) => {
          if (!startStr || !endStr) return '';
          // Convert to ISO format first to handle DD-MM-YYYY from OCR
          const startISO = formatDateForInput(startStr);
          const endISO = formatDateForInput(endStr);
          if (!startISO || !endISO) return '';
          const s = parseLocalISOToDate(startISO);
          const eDate = parseLocalISOToDate(endISO);
          if (!s || !eDate || Number.isNaN(s.getTime()) || Number.isNaN(eDate.getTime())) return '';
          return Math.max(1, Math.round((eDate - s) / (1000 * 60 * 60 * 24)) + 1);
        };
        // Derive end date from start date + total days when end date is missing
        const deriveEndDate = (startStr, totalDays) => {
          if (!startStr || !totalDays) return '';
          const startISO = formatDateForInput(startStr);
          if (!startISO) return '';
          const startDate = parseLocalISOToDate(startISO);
          if (!startDate || Number.isNaN(startDate.getTime())) return '';
          const days = parseInt(totalDays, 10);
          if (!days || days < 1) return '';
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + (days - 1));
          return toLocalISODate(endDate);
        };
        const mapToExtracted = (e) => {
          const totalDays = e?.ocr_mc_total_days ?? e?.total_days ?? calcDays(e?.start_date, e?.end_date) ?? '';
          // Derive end date if missing but start date and total days are available
          const endDate = e?.end_date || deriveEndDate(e?.start_date, totalDays);
          return {
            ocr_patient_nric_fin: e?.nric_fin || '',
            ocr_patient_full_name: e?.full_name || '',
            ocr_mc_number: e?.mc_number || '',
            ocr_mc_issue_date: e?.issue_date || '',
            ocr_mc_start_date: e?.start_date || '',
            ocr_mc_end_date: endDate,
            ocr_doctor_name: e?.doctor_name || '',
            ocr_clinic_hospital_name: e?.clinic_hospital_name || '',
            ocr_diagnosis: e?.diagnosis || '',
            ocr_mc_total_days: totalDays || ''
          };
        };

        if (ocr) {
          const extracted = mapToExtracted(ocr);
          setOcrStatus({ loading: false, data: { extracted_data: extracted }, error: null });
          
          // Auto-fill dates into form if hidden and empty, and attach OCR to submission data
          setFormData(prev => ({
            ...prev,
            leave_start_date: prev.leave_start_date || (extracted.ocr_mc_start_date ? formatDateForInput(extracted.ocr_mc_start_date) : ''),
            leave_end_date: prev.leave_end_date || (extracted.ocr_mc_end_date ? formatDateForInput(extracted.ocr_mc_end_date) : ''),
            ocr_data: extracted
          }));
        } else {
          // Fallback to polling backend if n8n doesn't return OCR payload
          setOcrStatus({ loading: true, data: null, error: null });
          let attempts = 0;
          const maxAttempts = 10;
          const poll = async () => {
            try {
              const res = await staffAPI.getMcVerificationData(applicationId);
              if (res?.data?.success) {
                setOcrStatus({ loading: false, data: res.data, error: null });
                const d = res.data?.extracted_data || {};
                
                setFormData(prev => ({
                  ...prev,
                  leave_start_date: prev.leave_start_date || (d.ocr_mc_start_date ? formatDateForInput(d.ocr_mc_start_date) : ''),
                  leave_end_date: prev.leave_end_date || (d.ocr_mc_end_date ? formatDateForInput(d.ocr_mc_end_date) : '')
                }));
                return;
              }
            } catch {}
            attempts += 1;
            if (attempts < maxAttempts) setTimeout(poll, 5000);
            else {
              setOcrStatus({ loading: false, data: null, error: 'Could not read your certificate. Please fill in the dates below.' });
              toast.error('Could not read your certificate. Please fill in the dates below.');
            }
          };
          setTimeout(poll, 3000);
        }
      } catch (e) {
        console.error('Failed to send MC to OCR webhook on upload:', e);
        const errorCode = e.response?.data?.error;
        if (e.response?.status === 409 && (errorCode === 'duplicate_mc' || errorCode === 'duplicate_mc_dates' || errorCode === 'duplicate_mc_number')) {
          if (applicationId) {
            try {
              await staffAPI.discardMcDraft(applicationId, { mode: 'self_service' });
              setDraftApplicationId(null);
            } catch (discardErr) {
              console.warn('Failed to discard duplicate self-service MC draft:', discardErr);
            }
          }
          const serverMessage = e.response?.data?.message;
          if (errorCode === 'duplicate_mc_dates') {
            const message = serverMessage || 'A medical certificate already exists for these dates.';
            setOcrStatus({ loading: false, data: null, error: message });
            toast.error(message, { duration: 6000 });
          } else if (errorCode === 'duplicate_mc_number' || errorCode === 'duplicate_mc') {
            const message = serverMessage || 'This medical certificate has already been uploaded.';
            setOcrStatus({ loading: false, data: null, error: message });
            toast.error(message, { duration: 6000 });
          } else {
            setOcrStatus({ loading: false, data: null, error: 'This medical certificate has already been uploaded.' });
            toast.error('This medical certificate has already been uploaded for this leave type.', { duration: 6000 });
          }
        } else {
          setOcrStatus({ loading: false, data: null, error: 'Could not read your certificate. Please fill in the dates below.' });
          toast.error('Could not read your certificate. Please fill in the dates below.');
        }
      }

    } catch (error) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Camera capture handler
  const handleCameraCapture = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file, 'camera');
    }
  };

  // File input handler
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(file, 'file');
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    fieldConfig.requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    // Trim-aware check: whitespace-only input must also be rejected
    if (fieldConfig.requiredFields.includes('leave_reason_details') && !formData.leave_reason_details?.trim()) {
      newErrors.leave_reason_details = 'Please provide a specific reason when selecting "Other"';
    }
    if (
      isAnnual
      && futureYearProjection
      && !futureYearProjection.supported
    ) {
      newErrors.leave_start_date = futureYearProjection.reason
        || 'A reliable paid balance is not available for those dates.';
    }
    
    // MOM Compliance: Special validation for Saturday PH claims (Entitlement B - work done)
    // Work description is required for manual claims on Saturday PH (in addition to auto-credit Entitlement A)
    if (isOffInLieuClaim && formData.oil_dates && formData.oil_dates.length > 0) {
      const hasSaturdayPH = formData.oil_dates.some(entry => {
        if (!entry.date) return false;
        const validation = validateOILDate(entry.date);
        return validation.isSaturdayPH === true; // Saturday PH claim
      });
      
      if (hasSaturdayPH && !formData.work_description?.trim()) {
        newErrors.work_description = 
          'Work description is required when claiming OIL for work done on a Saturday Public Holiday. ' +
          'This is in addition to the automatic OIL credit for the PH falling on a non-working day (Entitlement A). ' +
          'Please provide details of the work performed (Entitlement B).';
      }
    }

    // Additional validation
    if (fieldConfig.showDates && formData.leave_start_date && formData.leave_end_date) {
      const start = parseLocalISOToDate(formData.leave_start_date);
      const end = parseLocalISOToDate(formData.leave_end_date);
      
      // Check if dates / half-day periods are valid
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
        newErrors.leave_end_date = 'Please enter valid dates';
      } else if (start > end) {
        newErrors.leave_start_date = 'Start date cannot be after the end date';
        newErrors.leave_end_date = 'End date must be on or after the start date';
      } else if (
        formData.leave_start_date === formData.leave_end_date &&
        formData.leave_start_time_period === 'PM' &&
        formData.leave_end_time_period === 'AM'
      ) {
        newErrors.leave_end_date =
          'End period cannot be before start period on the same day';
      }
    }

    // Block status-only leave types (WFH, External Activity, On Course) on rest days
    if (isRestDayBlock) {
      newErrors.leave_start_date = 'Selected date falls on a rest day or public holiday — cannot apply for this leave type.';
    }

    // "Claim: Off in Lieu" specific validation (accrual)
    if (isOffInLieuClaim) {
      // Validate oil_dates array
      if (!formData.oil_dates || formData.oil_dates.length === 0) {
        newErrors.oil_dates = 'Please add at least one work date';
      } else {
        const invalidDates = formData.oil_dates.some(entry => {
          if (!entry.date || !entry.days) return true;
          const validation = validateOILDate(entry.date);
          if (!validation.valid) return true;
          
          // Also check 30-day claim window
          const claimWindow = validateOILClaimWindow(entry.date);
          if (!claimWindow.isValid) return true;
          
          return false;
        });
        if (invalidDates) {
          newErrors.oil_dates = 'Please ensure all dates are valid, within 30-day claim window, and have Off in Lieu days selected';
        }
        // Check for duplicates
        const dateStrings = formData.oil_dates.map(e => e.date).filter(Boolean);
        const hasDuplicates = new Set(dateStrings).size !== dateStrings.length;
        if (hasDuplicates) {
          newErrors.oil_dates = 'Please remove duplicate dates';
        }
      }
    }

    // External Activity specific validation
    if (isExternalActivity) {

      
      // Validate other_activity_type when "Other" is selected
      if (formData.activity_type === 'Other' && !formData.other_activity_type?.trim()) {
        newErrors.other_activity_type = 'Please specify the activity type when selecting "Other"';
      }
      // Validate project selection: must be a known code or "Other"
      if (formData.project_code && formData.project_code !== 'Other' && !validProjectCodes.has(String(formData.project_code))) {
        newErrors.project_code = 'Select a valid project or choose "Other"';
      }
    }

    // Compassionate Leave specific validation
    if (isCompassionateLeave) {
      if (!formData.compassionate_sub_type) {
        newErrors.compassionate_sub_type = 'Please select Immediate Family or Grandparent';
      }
      if (!formData.compassionate_relationship) {
        newErrors.compassionate_relationship = 'Please select the relationship to the deceased';
      }
      if (!formData.deceased_name?.trim()) {
        newErrors.deceased_name = 'Please enter the name of the deceased';
      }
    }

    // On Course specific validation
    if (isOnCourse) {
      if (!formData.course_name?.trim()) {
        newErrors.course_name = 'Please enter the course or training name';
      }
      if (!formData.course_location?.trim()) {
        newErrors.course_location = 'Please enter the course location';
      }
    }

    Object.assign(newErrors, validateOverseasTravelFields({
      shouldPrompt: Boolean(overseasTravelPrompt?.should_prompt),
      declared: formData.overseas_travel_declared,
      departure: formData.overseas_departure_date,
      returnDate: formData.overseas_return_date,
    }));

    setErrors(newErrors);
    return newErrors;
  };

  const scrollToOverseasTravel = () => {
    window.requestAnimationFrame(() => {
      overseasTravelSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  };

  // Reveal the travel question as soon as the server says the window is
  // long enough — do not wait for Submit. Re-scroll only when the prompt
  // appears (false → true), not on every AM/PM recalc.
  const travelPromptWasVisibleRef = useRef(false);
  useEffect(() => {
    if (!overseasTravelPrompt.should_prompt) {
      travelPromptWasVisibleRef.current = false;
      return;
    }
    if (travelPromptWasVisibleRef.current) return;
    travelPromptWasVisibleRef.current = true;
    scrollToOverseasTravel();
  }, [overseasTravelPrompt.should_prompt]);

  // Form submission
  const handleSubmit = async (e, isDraft = false) => {
    e?.preventDefault(); // Only prevent default if e is provided (for form submission)
    
    if (isSubmitting) return;

    // Do not submit against a stale should_prompt=false while days are still
    // calculating — the server will 400 the overseas-travel question and the
    // employee lands on the list page with a dead-end toast.
    if (!isDraft && (calcLoading || calcFailed)) {
      toast.error(
        calcFailed
          ? 'Could not calculate leave days. Adjust the dates and try again.'
          : 'Leave days are still calculating. Please wait a moment.',
      );
      return;
    }

    // For drafts, we only require basic validation, not full validation
    if (!isDraft) {
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        toast.error('Please fix the errors below');
        if (hasOverseasTravelFieldErrors(validationErrors)) {
          scrollToOverseasTravel();
        }
        return;
      }
    }

    // For drafts, only validate required fields minimally
    if (isDraft && !formData.leave_type_id) {
      toast.error('Please select a leave type to save as draft');
      return;
    }

    // For sick/hospitalisation leave with MC upload, ensure minimal OCR fields are present
    if (!isDraft && showMcUpload && formData.leave_medical_cert_file) {
      const d = ocrStatus?.data?.extracted_data || {};
      const errors = [];
      if (!d.ocr_patient_nric_fin) errors.push('NRIC/FIN is required from MC');
      if (!d.ocr_patient_full_name) errors.push('Name is required from MC');
      if (!d.ocr_mc_number) errors.push('MC Number is required');
      if (!d.ocr_mc_start_date) errors.push('MC Start Date is required');
      
      // Require either End Date OR Total Days (one can be derived from the other)
      if (!d.ocr_mc_end_date && !d.ocr_mc_total_days) {
        errors.push('Provide either MC End Date or MC Total Days');
      }
      
      // If we have Start + one of (End or Total), derive the missing field before validation
      if (d.ocr_mc_start_date && (d.ocr_mc_end_date || d.ocr_mc_total_days)) {
        const parseDate = (dateStr) => {
          if (!dateStr) return null;
          const parsed = formatDateForInput(dateStr);
          if (!parsed) return null;
          const date = new Date(parsed);
          return isNaN(date.getTime()) ? null : date;
        };
        
        const startDate = parseDate(d.ocr_mc_start_date);
        const endDate = parseDate(d.ocr_mc_end_date);
        const totalDays = d.ocr_mc_total_days ? parseInt(d.ocr_mc_total_days, 10) : null;
        
        // Derive End Date if missing but Total Days present
        if (startDate && totalDays && totalDays >= 1 && !d.ocr_mc_end_date) {
          const calculatedEnd = new Date(startDate);
          calculatedEnd.setDate(calculatedEnd.getDate() + (totalDays - 1));
          const updatedData = { ...d, ocr_mc_end_date: toLocalISODate(calculatedEnd) };
          setOcrStatus(prev => ({ ...prev, data: { ...prev.data, extracted_data: updatedData } }));
        }
        
        // Derive Total Days if missing but End Date present
        if (startDate && endDate && !d.ocr_mc_total_days) {
          const diffTime = endDate - startDate;
          const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1; // Inclusive
          if (diffDays >= 1) {
            const updatedData = { ...d, ocr_mc_total_days: String(diffDays) };
            setOcrStatus(prev => ({ ...prev, data: { ...prev.data, extracted_data: updatedData } }));
          }
        }
      }
      
      if (errors.length > 0) {
        errors.forEach(msg => toast.error(msg));
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        leave_status: isDraft ? 'draft' : 'submitted',
        ...buildOverseasTravelPayload({
          shouldPrompt: Boolean(overseasTravelPrompt?.should_prompt),
          declared: formData.overseas_travel_declared,
          departure: formData.overseas_departure_date,
          returnDate: formData.overseas_return_date,
        }),
      };
      
      // Special handling for "Claim: Off in Lieu" - map OIL-specific fields to standard fields
      const selectedLeaveTypeForMapping = effectiveLeaveTypes.find(lt => lt.id === formData.leave_type_id);
      const leaveTypeNameForMapping = selectedLeaveTypeForMapping?.leave_type_name || selectedLeaveTypeForMapping?.name || '';
      
      if (leaveTypeNameForMapping === 'Claim: Off in Lieu') {
        // Set leave_total_days from calculated total OIL days
        submitData.leave_total_days = totalOILDays;
        
        // Set leave_reason from work_description (so it shows in detail view)
        submitData.leave_reason = formData.work_description || 'Off in Lieu Claim';
        
      }

      const selectedLeaveType = effectiveLeaveTypes.find(lt => lt.id === formData.leave_type_id);
      const leaveTypeName = selectedLeaveType?.leave_type_name || selectedLeaveType?.name || 'Unknown';

      if (!isDraft && showMcUpload && formData.leave_medical_cert_file && ocrStatus?.data?.extracted_data) {
        submitData.ocr_data = ocrStatus.data.extracted_data;
      }

      // Map Compassionate Leave fields & resolve correct backend leave_type_id
      if (isCompassionateLeaveName(leaveTypeName)) {
        submitData.compassionate_relationship = formData.compassionate_relationship || '';
        submitData.deceased_name = formData.deceased_name?.trim() || '';

        // Resolve the correct backend LeaveType based on selected sub-type
        const subType = formData.compassionate_sub_type;
        const resolvedType = subType === 'grandparent'
          ? compassionateLeaveTypeMap.grandparent
          : compassionateLeaveTypeMap.immediate_family;
        if (resolvedType) {
          submitData.leave_type_id = String(resolvedType.id);
        }

        const relLookupName = subType === 'grandparent' ? 'Grandparent' : 'Immediate Family';
        const relLabel = getAllCompassionateRelationships(relLookupName).find(r => r.value === formData.compassionate_relationship)?.label || formData.compassionate_relationship;
        submitData.leave_reason = `Bereavement: ${formData.deceased_name?.trim() || 'N/A'} (${relLabel})`;
        
        if (deathCertDocId) {
          submitData.document_approval_id = deathCertDocId;
        }
      }

      // Map On Course fields — include course_name and course_location in the payload
      if (isOnCourseName(leaveTypeName)) {
        submitData.course_name = formData.course_name?.trim() || '';
        submitData.course_location = formData.course_location?.trim() || '';
        // Auto-populate leave_reason for display in detail view
        submitData.leave_reason = `Course: ${submitData.course_name}` + (submitData.course_location ? ` (${submitData.course_location})` : '');
      }

      // Map WFH reason to leave_reason for display in detail/list/grid views
      if (isWFHLeaveName(leaveTypeName)) {
        const selectedWfhReason = WFH_REASONS.find(r => r.value === formData.wfh_reason);
        submitData.leave_reason = formData.wfh_reason === 'others'
          ? (formData.wfh_other_reason?.trim() || 'Others')
          : (selectedWfhReason?.label || formData.wfh_reason || '');
      }

      // Always persist the free-text detail to the dedicated DB column (draft and final).
      // This prevents data loss when a user saves a draft and reopens it later.
      submitData.leave_reason_details = formData.leave_reason_details?.trim() || '';

      // On final submission, also copy details into leave_reason so the Reporting Officer
      // sees the actual reason (not the literal string "Other") in approval views.
      if (!isDraft && formData.leave_reason === 'Other' && formData.leave_reason_details?.trim()) {
        submitData.leave_reason = formData.leave_reason_details.trim();
      }

      await onSubmit(submitData, isDraft, draftApplicationId);
      if (!isDraft) {
        setProposalDisplay('');
        setFutureYearProjection(null);
        setYearEndPriority(null);
        setBalanceWarning(null);
        setCalculatedLeaveDays(0);
        setFormData((prev) => ({
          ...prev,
          leave_start_date: '',
          leave_end_date: '',
        }));
      }
    } catch (error) {
      console.error('Error in leave application:', error);
      
      // Handle validation errors from backend
      if (error.response && error.response.status === 400) {
        const errorData = error.response.data || {};
        const overseasFieldErrors = extractOverseasTravelServerErrors(errorData);
        if (Object.keys(overseasFieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...overseasFieldErrors }));
          setOverseasTravelPrompt((prev) => (
            prev.should_prompt ? prev : { ...prev, should_prompt: true }
          ));
          toast.error('Please fix the errors below');
          scrollToOverseasTravel();
        }
        
        // Enhanced error handling for leave eligibility issues
        let hasEligibilityError = false;
        
        // Check for eligibility-related error messages
        if (errorData.non_field_errors) {
          errorData.non_field_errors.forEach(message => {
            // Check if this is an eligibility error
            if (message.includes('Not eligible for') && message.includes('Please apply for \'Unpaid Leave\' instead')) {
              hasEligibilityError = true;
              // Extract the leave type name from the error message
              const leaveTypeMatch = message.match(/Not eligible for ([^.]+)/);
              const leaveTypeName = leaveTypeMatch ? leaveTypeMatch[1].trim() : 'this leave type';
              
              toast.error(
                `❌ Eligibility Check Failed\n\nYou are not eligible for ${leaveTypeName}.\n\nPlease apply for "Unpaid Leave" instead.`,
                { 
                  duration: 8000,
                  position: 'top-center'
                }
              );
            } else {
              toast.error(message);
            }
          });
        } else if (errorData.error_type === 'eligibility_check_failed') {
          // Handle structured eligibility error response
          hasEligibilityError = true;
          const leaveTypeName = errorData.leave_type_name || 'this leave type';
          const serviceMonths = errorData.service_months || 0;
          const detailedMessage = errorData.message || `You are not eligible for ${leaveTypeName}. Please apply for "Unpaid Leave" instead.`;
          
          // Show more detailed error message based on service months
          if (serviceMonths === 0) {
            toast.error(
              `❌ Eligibility Check Failed\n\n${detailedMessage}\n\nIf you believe this is an error, please contact HR.`,
              { 
                duration: 10000,
                position: 'top-center'
              }
            );
          } else {
            toast.error(
              `❌ Eligibility Check Failed\n\n${detailedMessage}`,
              { 
                duration: 8000,
                position: 'top-center'
              }
            );
          }
        } else if (errorData.error) {
          // Check if the error message contains eligibility information
          if (errorData.error.includes('Not eligible for') && errorData.error.includes('Please apply for \'Unpaid Leave\' instead')) {
            hasEligibilityError = true;
            const leaveTypeMatch = errorData.error.match(/Not eligible for ([^.]+)/);
            const leaveTypeName = leaveTypeMatch ? leaveTypeMatch[1].trim() : 'this leave type';
            
            toast.error(
              `❌ Eligibility Check Failed\n\nYou are not eligible for ${leaveTypeName}.\n\nPlease apply for "Unpaid Leave" instead.`,
              { 
                duration: 8000,
                position: 'top-center'
              }
            );
          } else {
            toast.error(errorData.error);
          }
        } else if (errorData.error_type === 'date_validation_error') {
          // Handle date validation errors
          toast.error(
            `📅 Date Validation Error\n\n${errorData.message}`,
            { 
              duration: 6000,
              position: 'top-center'
            }
          );
        } else if (errorData.error_type === 'leave_type_error') {
          // Handle leave type errors
          toast.error(
            `❌ Leave Type Error\n\n${errorData.message}`,
            { 
              duration: 6000,
              position: 'top-center'
            }
          );
        } else if (errorData.error_type === 'insufficient_balance') {
          // Handle insufficient balance errors
          toast.error(
            `💰 Insufficient Leave Balance\n\n${errorData.message}`,
            { 
              duration: 8000,
              position: 'top-center'
            }
          );
        } else {
          // Display field-specific errors (travel fields already inlined above)
          const fieldErrors = Object.entries(errorData)
            .filter(([key, value]) => Array.isArray(value))
            .filter(([key]) => !overseasFieldErrors[key])
            .map(([key, value]) => `${key}: ${value.join(', ')}`);
          
          if (fieldErrors.length > 0) {
            fieldErrors.forEach(error => toast.error(error));
          } else if (Object.keys(overseasFieldErrors).length === 0) {
            toast.error('Please check the form for errors');
          }
        }
        
        // If it's an eligibility error, we could potentially auto-switch to Unpaid Leave
        // but for now, we'll just show the error message
      } else {
        toast.error(isDraft ? 'Failed to save draft' : 'Failed to submit application');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Stable handler for OCR data updates
  const handleOcrDataUpdate = useCallback((next) => {
    setOcrStatus(prev => ({ ...prev, data: { ...prev.data, extracted_data: next } }));
  }, []);

  // Memoized Row component for OCR extracted fields - defined outside render to maintain stable identity
  const OcrDataRow = useMemo(() => {
    return React.memo(({ label, value, fieldKey, extractedData, onUpdate, onFormDataUpdate }) => {
      const isDateField = fieldKey === 'ocr_mc_issue_date' || fieldKey === 'ocr_mc_start_date' || fieldKey === 'ocr_mc_end_date';
      
      // Helper to convert value to Date object for DatePicker
      const getDateValue = () => {
        if (!value) return null;
        const isoDate = formatDateForInput(value) || value;
        return parseLocalISOToDate(isoDate);
      };
      
      // Handle date field changes
      const handleDateChange = (date) => {
        const isoDate = date ? toLocalISODate(date) : '';
        const next = { ...extractedData };
        next[fieldKey] = isoDate;
        
        // Auto-derivation logic for MC dates/total days
        if (fieldKey === 'ocr_mc_start_date' || fieldKey === 'ocr_mc_end_date') {
          const currentData = extractedData || {};
          const startStr = fieldKey === 'ocr_mc_start_date' ? isoDate : currentData.ocr_mc_start_date;
          const endStr = fieldKey === 'ocr_mc_end_date' ? isoDate : currentData.ocr_mc_end_date;
          
          // Parse dates
          const parseDate = (dateStr) => {
            if (!dateStr) return null;
            const parsed = formatDateForInput(dateStr);
            if (!parsed) return null;
            const d = new Date(parsed);
            return isNaN(d.getTime()) ? null : d;
          };
          
          const startDate = parseDate(startStr);
          const endDate = parseDate(endStr);
          
          // When user edits Start or End Date → Always recalculate Total Days
          if (startDate && endDate) {
            const diffTime = endDate - startDate;
            const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1; // Inclusive
            if (diffDays >= 1) {
              next.ocr_mc_total_days = String(diffDays);
            }
          }
        }
        
        onUpdate(next);
        
        // Keep form dates in sync for submission
        if (fieldKey === 'ocr_mc_start_date' || fieldKey === 'ocr_mc_end_date') {
          if (fieldKey === 'ocr_mc_start_date') {
            onFormDataUpdate(prev => ({ ...prev, leave_start_date: isoDate }));
          } else if (fieldKey === 'ocr_mc_end_date') {
            onFormDataUpdate(prev => ({ ...prev, leave_end_date: isoDate }));
          }
        }
      };
      
      // Handle text field changes
      const handleTextChange = (e) => {
        const next = { ...extractedData };
        next[fieldKey] = e.target.value;
        
        // Auto-derivation logic for MC Total Days → Recalculate End Date
        if (fieldKey === 'ocr_mc_total_days') {
          const currentData = extractedData || {};
          const startStr = currentData.ocr_mc_start_date;
          const totalStr = e.target.value;
          
          // Parse dates
          const parseDate = (dateStr) => {
            if (!dateStr) return null;
            const parsed = formatDateForInput(dateStr);
            if (!parsed) return null;
            const d = new Date(parsed);
            return isNaN(d.getTime()) ? null : d;
          };
          
          const startDate = parseDate(startStr);
          const totalDays = totalStr ? parseInt(totalStr, 10) : null;
          
          // When user edits Total Days → Recalculate End Date (if Start present)
          if (startDate && totalDays && totalDays >= 1) {
            const calculatedEnd = new Date(startDate);
            calculatedEnd.setDate(calculatedEnd.getDate() + (totalDays - 1));
            const endISO = toLocalISODate(calculatedEnd);
            next.ocr_mc_end_date = endISO;
          }
        }
        
        onUpdate(next);
      };
      
      return (
        <div className="flex justify-between items-center text-sm py-2">
          <span className="text-gray-600">{label}</span>
          {isDateField ? (
            <div className="w-full max-w-[180px]">
              <DatePickerField
                theme="employee"
                showIcon={false}
                selected={getDateValue()}
                onChange={handleDateChange}
                className="input h-9 text-sm w-full rounded-full px-3 py-1.5"
                placeholderText="-"
                portalId="datepicker-portal"
                popperClassName="datepicker-popper-high-z"
                popperPlacement="bottom-start"
              />
            </div>
          ) : (
            <input
              type="text"
              className="input h-9 text-sm w-full max-w-[180px] bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-left px-3 py-1.5"
              value={value || ''}
              onChange={handleTextChange}
              placeholder="-"
            />
          )}
        </div>
      );
    });
  }, []); // Empty deps - component factory is stable

  // Pre-close handler: if the user has an unsubmitted draft with an uploaded MC,
  // intercept the close to ask if they want to discard the upload.
  const handleCloseRequest = () => {
    if (draftApplicationId && formData.leave_medical_cert_file && !isSubmitting) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  // Awaited discard with fail-open close behavior:
  // if cleanup fails, still close the modal so users are never trapped.
  // 409 means the draft was already promoted to a real application; close silently.
  const handleConfirmDiscard = async () => {
    await safeDiscardAndClose({
      discardAction: () => staffAPI.discardMcDraft(draftApplicationId, { mode: 'self_service' }),
      isIgnorableError: (e) => e?.response?.status === 409,
      onBeforeClose: () => setShowDiscardConfirm(false),
      onClose: () => onClose(),
      failureMessage: 'Could not fully discard your upload. You can continue; cleanup will be retried later.',
      logContext: 'Failed to discard self-service MC draft during modal close:',
    });
  };

  const handleLeaveTypeChange = useCallback(async (newValue) => {
    const selectedType = effectiveLeaveTypes.find(t => String(t.id) === String(newValue));
    const isExtAct = selectedType && isExternalActivityName(selectedType.name || selectedType.leave_type_name);
    const selNameLower = (selectedType?.leave_type_name || selectedType?.name || '').toLowerCase();

    const workerLeaveTypes = ['home leave', 'annual leave', 'urgent leave'];
    if (workerLeaveTypes.includes(selNameLower) && typeof onSwitchToWorkerLeave === 'function') {
      onSwitchToWorkerLeave(selNameLower);
      return;
    }

    const previousType = effectiveLeaveTypes.find(t => String(t.id) === String(formData.leave_type_id));
    const previousName = previousType?.leave_type_name || previousType?.name || '';
    const nextName = selectedType?.leave_type_name || selectedType?.name || '';
    const leavingSickLeave = isSickLeaveName(previousName) && !isSickLeaveName(nextName);
    const hadMcUpload = !!(formData.leave_medical_cert_file || draftApplicationId || ocrStatus?.data);

    if (leavingSickLeave && hadMcUpload && draftApplicationId) {
      try {
        await staffAPI.detachMcFromApplication(draftApplicationId);
      } catch (e) {
        console.warn('Failed to detach MC when switching leave type:', e);
      }
      setDraftApplicationId(null);
      setOcrStatus({ loading: false, data: null, error: null });
    }

    setFormData(prev => ({
      ...prev,
      leave_type_id: newValue,
      leave_reason: '',
      leave_reason_details: '',
      leave_medical_cert_file: null,
      leave_start_date: '',
      leave_end_date: '',
      leave_start_time_period: isExtAct ? 'AM' : 'AM',
      leave_end_time_period: isExtAct ? 'AM' : 'PM',
      saturday_work_date: '',
      work_description: '',
      project_code: '',
      project_name: '',
      oil_days: '',
      oil_dates: [],
      activity_type: '',
      other_activity_type: '',
      activity_location: '',
      activity_organizer: '',
      business_justification: '',
      wfh_reason: '',
      wfh_other_reason: '',
      course_name: '',
      course_location: '',
      compassionate_sub_type: '',
      compassionate_relationship: '',
      deceased_name: '',
      overseas_travel_declared: null,
      overseas_departure_date: '',
      overseas_return_date: '',
    }));
    setMcPreview(null);
    setErrors({});
  }, [effectiveLeaveTypes, onSwitchToWorkerLeave, formData.leave_type_id, formData.leave_medical_cert_file, draftApplicationId, ocrStatus?.data]);

  const leaveTypeOptions = useMemo(() => {
    if (isLoading) return [{ value: '', label: '⏳ Loading leave types...' }];
    if (displayLeaveTypes.length === 0) return [{ value: '', label: '❌ No leave types available for your profile' }];
    return [
      { value: '', label: '🔍 Select your leave type' },
      ...displayLeaveTypes
        .filter(t => !!getLeaveTypeName(t) || !!getLeaveTypeCode(t))
        .map((type) => {
          const isPaid = typeof type.is_paid === 'boolean'
            ? type.is_paid
            : (typeof type.leave_type_is_paid === 'boolean' ? type.leave_type_is_paid : true);
          const statutoryDays = type.leave_type_statutory_days ?? null;
          const rawName = getLeaveTypeName(type);
          const code = getLeaveTypeCode(type);
          const name = rawName || codeToDisplayName[code] || code || '';
          const isUnpaidName = /(^|\s)unpaid\s*leave(\s|$)/i.test(name);
          const isClaim = type.is_claim || name.toLowerCase().startsWith('claim:');
          const unpaidSuffix = !isPaid && !isUnpaidName && !isClaim ? ' - Unpaid' : '';
          const displayName = isCompassionateLeaveName(name) ? 'Compassionate Leave' : name;
          const displayDays = isCompassionateLeaveName(name) ? null : statutoryDays;
          const label = `${displayName}${displayDays ? ` (${displayDays} days)` : ''}${unpaidSuffix}`;
          return { value: String(type.id), label };
        })
    ];
  }, [isLoading, displayLeaveTypes]);

  const overseasTravelFields = (
    <div ref={overseasTravelSectionRef} id="overseas-travel-declaration">
      <OverseasTravelDeclarationFields
        prompt={overseasTravelPrompt}
        declared={formData.overseas_travel_declared}
        departureDate={formData.overseas_departure_date}
        returnDate={formData.overseas_return_date}
        deductedLeaveDays={calculatedLeaveDays}
        errors={errors}
        onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
      />
    </div>
  );

  if (!isOpen) return null;

  // If NS Leave is selected, render the specialized NS Leave modal
  if (isNSLeave) {
    return (
      <NSLeaveApplicationModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onSubmit}
        editingLeave={editingLeave}
        availableLeaveTypes={effectiveLeaveTypes}
        leaveTypeOptions={leaveTypeOptions}
        selectedLeaveTypeId={String(formData.leave_type_id ?? '')}
        onChangeLeaveType={handleLeaveTypeChange}
      />
    );
  }

  return (
    <>
      {/* Discard confirmation — shown when user closes with an unsubmitted MC upload.
          theme="employee" (navy) per design system — this is an employee-facing surface. */}
      <ConfirmModal
        isOpen={showDiscardConfirm}
        title="Discard uploaded document?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        variant="danger"
        theme="employee"
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
        isLoading={isDiscarding}
      >
        <p className="text-sm text-gray-700">
          Your uploaded medical certificate and any extracted data will be removed.
          You can re-upload when you are ready to submit.
        </p>
      </ConfirmModal>

      <Modal
        isOpen={isOpen}
        onClose={handleCloseRequest}
        size="lg"
        theme="employee"
        closable={false}
        customHeader={(
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              {isSick ? (
                <Stethoscope className="w-5 h-5 text-blue-600" />
              ) : isWFH ? (
                <Home className="w-5 h-5 text-blue-600" />
              ) : isCompassionateLeave ? (
                <Heart className="w-5 h-5 text-blue-600" />
              ) : isOnCourse ? (
                <GraduationCap className="w-5 h-5 text-blue-600" />
              ) : isExternalActivity ? (
                <MapPin className="w-5 h-5 text-blue-600" />
              ) : isOffInLieu ? (
                <Clock className="w-5 h-5 text-blue-600" />
              ) : (
                <Calendar className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <div>
              <h3 className="text-subsection-header">
                {editingLeave ? 'Edit Leave Application' : 'Apply for Leave'}
              </h3>
              <p className="text-sm text-gray-600">
                {(() => {
                  const displayLabel = selectedName || codeToDisplayName[getLeaveTypeCode(selectedLeaveType)] || '';
                  return displayLabel
                    ? `Applying for: ${displayLabel}`
                    : 'Select a leave type to get started.';
                })()}
              </p>
            </div>
          </div>
          <button onClick={handleCloseRequest} className="modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        )}
        footer={formData.leave_type_id ? (
          <div className="flex flex-col w-full gap-3">
            {/* Primary Submit Button - Full Width */}
            {editingLeave && editingLeave.leave_status === 'draft' ? (
              <button
                type="button"
                disabled={isSubmitting || leaveCalcBlocking}
                onClick={() => handleSubmit(null, false)}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                {isSubmitting || calcLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {isSubmitting ? 'Submitting...' : calcLoading ? 'Calculating…' : 'Submit Application'}
              </button>
            ) : (
              ((!isDateOrderInvalid && (!conflictCheck.hasConflicts || isSick) && !(isSick && momValidation.errors && momValidation.errors.length > 0) && !isRestDayBlock && !calcFailed)) ? (
                <button type="submit" form="leave-application-form" disabled={isSubmitting || leaveCalcBlocking} className="btn btn-primary w-full flex items-center justify-center gap-2">
                  {isSubmitting || calcLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {isSubmitting ? 'Submitting...' : calcLoading ? 'Calculating…' : 'Submit Application'}
                </button>
              ) : (
                <button type="button" disabled className="btn btn-primary w-full flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" />
                  {isSick && momValidation.errors && momValidation.errors.length > 0
                    ? 'Cannot Submit - MOM Violation'
                    : isDateOrderInvalid
                      ? 'Cannot Submit - Invalid Dates'
                      : calcFailed
                        ? 'Cannot Submit - Recalculate'
                      : 'Submit Application'}
                </button>
              )
            )}
          </div>
        ) : null}
      >
          <form id="leave-application-form" onSubmit={handleSubmit} className="space-y-8">
            
            {/* Leave Type Selection */}
            <div className="space-y-4">

              <div className="form-group">
                <label className="form-label">
                  <Calendar className="inline w-4 h-4 mr-2" />
                  Leave Type
                </label>
                <FormSelect
                  value={String(formData.leave_type_id ?? '')}
                  onChange={handleLeaveTypeChange}
                  options={leaveTypeOptions}
                  theme="employee"
                  placeholder="🔍 Select your leave type"
                />
              {errors.leave_type_id && (
                <p className="text-red-600 text-sm mt-2">{errors.leave_type_id}</p>
              )}
              
              {/* Unpaid sick leave warning for probationary employees (Option 2 - Concise) */}
              {selectedLeaveType && selectedLeaveType.will_be_unpaid && selectedLeaveType.category === 'sick' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-orange-800">
                      <strong>Notice:</strong> This {selectedLeaveType.name.toLowerCase()} will be processed as unpaid leave because you have not completed 3 months of service. Medical certificate is still required.
                    </div>
                  </div>
                </div>
              )}
              
              {sortedLeaveTypes.length === 0 && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>No eligible leave types found.</strong> This may be because:
                  </p>
                  <ul className="text-xs text-yellow-700 mt-2 ml-4 list-disc space-y-1">
                    <li>You haven't completed your HR profile</li>
                    <li>Required documents (birth certificate, marriage certificate) need approval</li>
                    <li>You don't meet service requirements for certain leave types</li>
                    <li>Your employment classification doesn't qualify for available leave types</li>
                  </ul>
                  <p className="text-xs text-yellow-700 mt-2">
                    Contact HR for assistance with leave eligibility.
                  </p>
                </div>
              )}
              </div>

              {/* Dynamic Help Text */}
              {/* Removed confirmation help text to reduce visual noise */}
            </div>

            {/* Form fields - Only shown after leave type is selected */}
            {formData.leave_type_id && (
            <>
            {/* Medical Certificate Upload - Only for Sick/Hospitalisation Leave */}
            {fieldConfig.showMcUpload && (
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">
                    <FileText className="inline w-4 h-4 mr-2" />
                    Medical Certificate
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  


                {!formData.leave_medical_cert_file ? (
                  <div className="space-y-4">
                    {/* Upload Options - Side by side on all screen sizes */}
                    <div className="grid grid-cols-2 gap-4">
                      
                      {/* Choose File Option */}
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full bg-white hover:bg-gray-50 text-gray-700 rounded-xl py-4 px-3 border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center gap-2"
                        >
                          <Upload className="w-6 h-6 text-blue-600" />
                          <span className="font-medium text-sm">Choose File</span>
                        </button>
                      </div>

                      {/* Use Camera Option */}
                      <div>
                        <input
                          ref={cameraInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handleCameraCapture}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => cameraInputRef.current?.click()}
                          className="w-full bg-white hover:bg-gray-50 text-gray-700 rounded-xl py-4 px-3 border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center gap-2"
                        >
                          <Smartphone className="w-6 h-6 text-blue-600" />
                          <span className="font-medium text-sm">Use Camera</span>
                        </button>
                      </div>
                    </div>

                    {/* Upload Progress */}
                    {isSubmitting && uploadProgress > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-600">Uploading...</span>
                          <span className="text-sm font-medium text-blue-600">{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {ocrStatus.error && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-red-700 text-sm flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{ocrStatus.error}</span>
                      </div>
                    )}

                    {/* File Requirements */}
                    <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                      <p className="text-xs font-medium text-yellow-800 mb-1">📋 Requirements:</p>
                      <ul className="text-xs text-yellow-700 space-y-1">
                        <li>• Clear image of medical certificate</li>
                        <li>• All text must be readable</li>
                        <li>• Maximum file size: 5MB</li>
                        <li>• Accepted formats: JPG, PNG, PDF</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  // MC Preview Section - Ultra-minimal confirmation
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                      <span className="text-sm font-medium text-green-800">Medical Certificate Uploaded</span>
                    </div>

                    {/* OCR Processing Indicator - show only while loading */}
                    {ocrStatus.loading && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-start gap-2">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mt-0.5"></div>
                          <div>
                            <p className="text-sm font-medium text-blue-800">Analysing your medical certificate...</p>
                            <p className="text-xs text-blue-600 mt-1">
                              Please keep this modal open
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* OCR Result Preview */}
                    {ocrStatus.error && (
                      <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200 text-red-700 text-sm">
                        {ocrStatus.error}
                      </div>
                    )}
                    {ocrStatus.data && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="mb-2">
                          <p className="text-sm font-semibold text-green-800 mb-1 flex items-center gap-2">
                            <Stethoscope className="w-4 h-4" />
                            Extracted from Medical Certificate
                          </p>
                          <p className="text-xs text-green-700 italic">Please verify and correct if needed</p>
                        </div>
                        {(() => {
                          const d = ocrStatus.data.extracted_data || {};
                          
                          return (
                            <div>
                              {/* Patient Identification */}
                              <OcrDataRow 
                                label="NRIC / FIN" 
                                value={d.ocr_patient_nric_fin} 
                                fieldKey="ocr_patient_nric_fin"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                              <OcrDataRow 
                                label="Name" 
                                value={d.ocr_patient_full_name} 
                                fieldKey="ocr_patient_full_name"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                              
                              {/* Leave Period (Most Important - Prioritized) */}
                              <OcrDataRow 
                                label="MC Start Date" 
                                value={d.ocr_mc_start_date} 
                                fieldKey="ocr_mc_start_date"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                              <OcrDataRow 
                                label="MC End Date" 
                                value={d.ocr_mc_end_date} 
                                fieldKey="ocr_mc_end_date"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                              <OcrDataRow 
                                label="MC Total Days" 
                                value={d.ocr_mc_total_days} 
                                fieldKey="ocr_mc_total_days"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                              
                              {/* Document Metadata */}
                              <OcrDataRow 
                                label="MC Issue Date" 
                                value={d.ocr_mc_issue_date} 
                                fieldKey="ocr_mc_issue_date"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                              <OcrDataRow 
                                label="MC Number" 
                                value={d.ocr_mc_number} 
                                fieldKey="ocr_mc_number"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                              
                              {/* Provider Information */}
                              <OcrDataRow 
                                label="Doctor" 
                                value={d.ocr_doctor_name} 
                                fieldKey="ocr_doctor_name"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                              <OcrDataRow 
                                label="Clinic/Hospital" 
                                value={d.ocr_clinic_hospital_name} 
                                fieldKey="ocr_clinic_hospital_name"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                              
                              {/* Medical Context */}
                              <OcrDataRow 
                                label="Diagnosis" 
                                value={d.ocr_diagnosis} 
                                fieldKey="ocr_diagnosis"
                                extractedData={d}
                                onUpdate={handleOcrDataUpdate}
                                onFormDataUpdate={setFormData}
                              />
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {errors.leave_medical_cert_file && (
                  <p className="text-red-600 text-sm mt-2">{errors.leave_medical_cert_file}</p>
                )}
                </div>
              </div>
            )}

            {/* Date Selection - Only for non-MC leave types */}
            {fieldConfig.showDates && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="form-label flex items-center">
                    <Calendar className="inline w-4 h-4 mr-2" />
                    Total Days
                  </label>
                  
                  {/* Real-time Leave Days Calculation with OIL→AL→UL split when applicable */}
                  {calcLoading ? (
                    <InfoBadge color="blue" label="Calculating…" size="md" />
                  ) : calcFailed ? (
                    <InfoBadge color="red" label="Calculation failed" size="md" />
                  ) : calculatedLeaveDays > 0 ? (
                    <InfoBadge
                      color={balanceWarning?.type === 'unpaid_notice' ? 'amber' : 'blue'}
                      label={`${calculatedLeaveDays} ${pluralizeDays(calculatedLeaveDays)}${proposalDisplay && isAnnual ? ` (${proposalDisplay})` : ''}`}
                      size="md"
                    />
                  ) : null}
                </div>

                {calcFailed && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" aria-hidden="true" />
                      <p className="text-sm text-red-700">
                        Could not calculate leave days. Change the dates to try again before submitting.
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Invalid date/period range — highest priority; do not show rest-day/conflict noise */}
                {isDateOrderInvalid && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-red-800 mb-1">
                          {isPeriodOrderInvalid ? 'Invalid Half-Day Range' : 'Invalid Date Range'}
                        </h4>
                        <p className="text-sm text-red-700">
                          {isPeriodOrderInvalid
                            ? 'On the same day, the end period cannot be before the start period (PM → AM). Choose AM→AM, AM→PM, or PM→PM, or extend the end date.'
                            : 'Start date cannot be after the end date. Please set the end date on or after the start date to continue.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rest Day Warning for status-only leave types */}
                {isRestDayBlock && !isDateOrderInvalid && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-red-800 mb-1">Rest Day / Public Holiday</h4>
                        <p className="text-sm text-red-700">
                          The selected date falls on a rest day or public holiday for your work schedule. {selectedName} can only be applied on scheduled working days.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {showProjectedAnnualCard && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Info
                        className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-blue-900">
                          {annualLeaveStartsInProjectedYear
                            ? `Using your projected ${futureYearProjection.balance_year} balance`
                            : `Days from 1 January ${futureYearProjection.balance_year} use your projected balance`}
                        </h4>
                        <p className="text-sm text-blue-800 mt-1">
                          {annualLeaveStartsInProjectedYear
                            ? `Applying before 1 January does not reduce your ${futureYearProjection.source_year} balance.`
                            : `Days before 1 January come out of your ${futureYearProjection.source_year} Annual Leave. Days from 1 January use this projected balance and do not reduce your ${futureYearProjection.source_year} balance.`}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-blue-800">
                      <p>
                        {formatLeaveDays(futureYearProjection.projected_carry_forward_days)} days expected from {futureYearProjection.source_year} carry-forward
                        {' · '}
                        {formatLeaveDays(futureYearProjection.projected_earned_days)} days earned by your leave date
                      </p>
                      {Number(futureYearProjection.reserved_days_before_request || 0) > 0 && (
                        <p>
                          {formatLeaveDays(futureYearProjection.reserved_days_before_request)} days are already reserved for other {futureYearProjection.balance_year} leave.
                        </p>
                      )}
                      <p className="font-medium text-blue-900">
                        {formatLeaveDays(futureYearProjection.annual_leave_days_in_request)} Annual Leave {pluralizeDays(futureYearProjection.annual_leave_days_in_request)}{annualLeaveStartsInProjectedYear ? '' : ` in ${futureYearProjection.balance_year}`} reserved
                        {' · '}
                        {formatLeaveDays(futureYearProjection.projected_remaining_after_request)} days projected remaining
                      </p>
                      {Number(futureYearProjection.projected_forfeiture_days || 0) > 0 && (
                        <p className="font-medium text-blue-900">
                          Your {futureYearProjection.source_year} balance is {formatLeaveDays(futureYearProjection.projected_forfeiture_days)} {pluralizeDays(futureYearProjection.projected_forfeiture_days)} above the carry-forward limit. This excess must be used by 31 December {futureYearProjection.source_year}. It will not carry into {futureYearProjection.balance_year} and cannot be used for leave dates from 1 January {futureYearProjection.balance_year}.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {yearEndPriority?.applied && isAnnual && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <CalendarRange
                        className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-blue-900">
                          Using Annual Leave before it expires
                        </h4>
                        <p className="text-sm text-blue-800 mt-1">
                          You have {formatLeaveDays(yearEndPriority.at_risk_al_days)} {pluralizeDays(yearEndPriority.at_risk_al_days)} projected above the {formatLeaveDays(yearEndPriority.carry_forward_cap)}-day carry-forward limit. This request uses that Annual Leave before {formatLeaveDays(yearEndPriority.oil_days_valid_after_year_end)} {pluralizeDays(yearEndPriority.oil_days_valid_after_year_end)} of OIL that remains valid into {yearEndPriority.destination_year}.
                        </p>
                        <p className="text-xs text-blue-700 mt-2">
                          The excess Annual Leave expires on 31 December {yearEndPriority.source_year}; your later-expiring OIL is preserved.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {futureYearProjection && !futureYearProjection.supported && isAnnual && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-amber-900">
                          Future paid balance is not available
                        </h4>
                        <p className="text-sm text-amber-800 mt-1">
                          {futureYearProjection.reason || 'Choose dates in the next calendar year or contact HR.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Unpaid Leave Notice for Annual Leave */}
                {balanceWarning && balanceWarning.type === 'unpaid_notice' && isAnnual && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-2">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-amber-800 mb-1">Unpaid Leave Notice</h4>
                        <p className="text-sm text-amber-700">
                          <strong>{balanceWarning.unpaidDays} {pluralizeDays(balanceWarning.unpaidDays)}</strong> of this request will be processed as <strong>Unpaid Leave</strong> due to insufficient paid leave balance.
                        </p>
                        <p className="text-xs text-amber-600 mt-2">
                          Your salary will be deducted accordingly for the unpaid days.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Start Date */}
                  <div className="form-group">
                    <label className="form-label">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-nowrap items-center gap-2 min-w-0">
                      <div className="flex-1 min-w-0">
                        <DatePickerField
                          theme="employee"
                          showIcon={false}
                          selected={formData.leave_start_date ? parseLocalISOToDate(formData.leave_start_date) : null}
                          onChange={(date) => {
                            const formattedDate = date ? toLocalISODate(date) : '';
                            setFormData(prev => ({
                              ...prev,
                              leave_start_date: formattedDate
                            }));
                          }}
                          filterDate={isStatusOnlyType ? isWorkingDay : undefined}
                          className={`input w-full rounded-full px-4 ${isDateOrderInvalid || errors.leave_start_date ? 'border-red-500' : ''}`}
                          placeholderText="Select start date"
                          required
                          portalId="datepicker-portal"
                          popperClassName="datepicker-popper-high-z"
                          popperPlacement="bottom-start"
                        />
                      </div>
                      <LeaveHalfDaySegmentedControl
                        theme="employee"
                        value={formData.leave_start_time_period}
                        onChange={(period) =>
                          setFormData((prev) => ({ ...prev, leave_start_time_period: period }))
                        }
                        ariaLabel="First day: morning or afternoon"
                      />
                    </div>
                    {(errors.leave_start_date || isCalendarOrderInvalid) && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.leave_start_date || (isCalendarOrderInvalid ? 'Start date cannot be after the end date' : null)}
                      </p>
                    )}
                  </div>
                  
                  {/* End Date */}
                  <div className="form-group">
                    <label className="form-label">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-nowrap items-center gap-2 min-w-0">
                      <div className="flex-1 min-w-0">
                        <DatePickerField
                          theme="employee"
                          showIcon={false}
                          selected={formData.leave_end_date ? parseLocalISOToDate(formData.leave_end_date) : null}
                          onChange={(date) => setFormData(prev => ({
                            ...prev,
                            leave_end_date: date ? toLocalISODate(date) : ''
                          }))}
                          filterDate={isStatusOnlyType ? isWorkingDay : undefined}
                          className={`input w-full rounded-full px-4 ${isDateOrderInvalid ? 'border-red-500' : ''}`}
                          placeholderText="Select end date"
                          minDate={formData.leave_start_date ? parseLocalISOToDate(formData.leave_start_date) : null}
                          required
                          portalId="datepicker-portal"
                          popperClassName="datepicker-popper-high-z"
                          popperPlacement="bottom-start"
                        />
                      </div>
                      <LeaveHalfDaySegmentedControl
                        theme="employee"
                        value={formData.leave_end_time_period}
                        onChange={(period) =>
                          setFormData((prev) => ({ ...prev, leave_end_time_period: period }))
                        }
                        ariaLabel="Last day: morning or afternoon"
                      />
                    </div>
                    {(errors.leave_end_date || isDateOrderInvalid) && (
                      <p className="text-red-600 text-sm mt-1">
                        {errors.leave_end_date ||
                          (isPeriodOrderInvalid
                            ? 'End period cannot be before start period on the same day'
                            : isCalendarOrderInvalid
                              ? 'End date must be on or after the start date'
                              : null)}
                      </p>
                    )}
                  </div>
                </div>

                {!isSick && !isDateOrderInvalid && (
                  <LeaveExcludedDaysCallout
                    excludedDates={excludedLeaveDates}
                    leaveTypeName={selectedName}
                    audience="employee"
                  />
                )}

                {overseasTravelFields}

                {/* Rest-day streak advisory (MOM s36) — WFH / External Activity count as
                    worked days toward the 12-day ceiling. Advisory only, never blocks submit. */}
                {showStreakAdvisory && (
                  <div
                    className={`rounded-lg p-4 border ${
                      restDayStreak.severity === 'critical' || restDayStreak.severity === 'high'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}
                  >
                    <div className="flex items-start">
                      {restDayStreak.severity === 'critical' || restDayStreak.severity === 'high' ? (
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <h4
                          className={`text-sm font-semibold mb-1 ${
                            restDayStreak.severity === 'critical' || restDayStreak.severity === 'high'
                              ? 'text-red-800'
                              : 'text-amber-800'
                          }`}
                        >
                          Consecutive Working Days
                        </h4>
                        <p
                          className={`text-sm ${
                            restDayStreak.severity === 'critical' || restDayStreak.severity === 'high'
                              ? 'text-red-700'
                              : 'text-amber-700'
                          }`}
                        >
                          {restDayStreak.message}
                        </p>
                        <p
                          className={`text-xs mt-2 ${
                            restDayStreak.severity === 'critical' || restDayStreak.severity === 'high'
                              ? 'text-red-600'
                              : 'text-amber-600'
                          }`}
                        >
                          {selectedName} counts as a working day under MOM Employment Act s36 — it does
                          not give you a rest day.
                          {restDayStreak.projected_breach_date && (
                            <> Without an actual rest day, you would breach the 12-day limit by{' '}
                              {formatDate(restDayStreak.projected_breach_date)}.</>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            )}

            {!fieldConfig.showDates && overseasTravelFields}

            {/* MOM COMPLIANCE: For Sick/Hosp leave with BLOCKING errors (red) - e.g., overlapping AL/UL or all non-working days */}
            {isSick && momValidation.errors && momValidation.errors.length > 0 && (
              <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-red-800 mb-2">⛔ MOM Regulation Violation - Cannot Submit</h4>
                    <p className="text-sm text-red-700 mb-3">
                      According to Singapore MOM regulations, employees are <strong>not eligible</strong> for paid sick leave during Annual Leave or Unpaid Leave periods.
                    </p>
                    
                    {/* Show specific errors */}
                    <div className="space-y-2 mb-3">
                      {momValidation.errors.map((error, index) => (
                        <div key={index} className="bg-white rounded border border-red-200 p-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 p-3 bg-red-100 rounded border border-red-200">
                      <p className="text-sm text-red-800">
                        <strong>What to do:</strong> Please select dates that do not overlap with existing Annual Leave or Unpaid Leave. 
                        If you have exceptional circumstances, please contact HR directly.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* MOM COMPLIANCE: For Sick/Hosp leave with excluded days (blue info) - rest days/public holidays */}
            {isSick && momValidation.blockedDates && momValidation.blockedDates.length > 0 && (!momValidation.errors || momValidation.errors.length === 0) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">ℹ️ MOM Regulation - Some Days Excluded</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Per MOM regulations, rest days and public holidays do not count toward sick leave. 
                      The following dates will be <strong>automatically excluded</strong> from your sick leave count:
                    </p>
                    
                    {/* Show excluded dates */}
                    <div className="flex flex-wrap gap-2">
                      {momValidation.blockedDates.slice(0, 5).map((blocked, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                          {blocked.date} ({blocked.day_name}) - {blocked.reason}
                        </span>
                      ))}
                      {momValidation.blockedDates.length > 5 && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                          +{momValidation.blockedDates.length - 5} more dates
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-3 p-2 bg-blue-100 rounded">
                      <p className="text-sm text-blue-800">
                        <strong>Your sick leave will count as {momValidation.workingDays || 0} working day(s)</strong> instead of {momValidation.blockedDates.length + (momValidation.workingDays || 0)} calendar days.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MOM COMPLIANCE: PH Forfeiture (Before & After / Sandwich Rule) Warning */}
            {phForfeiture.wouldForfeit && phForfeiture.forfeitedHolidays.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-amber-800 mb-2">MOM Regulation - Public Holiday Pay Forfeiture</h4>
                    <p className="text-sm text-amber-700 mb-3">
                      Under the Employment Act &quot;Before &amp; After&quot; rule, you will <strong>not be paid</strong> for the following public holiday(s) because they fall within your unpaid leave block:
                    </p>
                    <div className="space-y-2 mb-3">
                      {phForfeiture.forfeitedHolidays.map((holiday, index) => (
                        <div key={index} className="bg-white rounded border border-amber-200 p-3">
                          <div className="text-sm text-amber-800 flex items-center gap-2">
                            <span className="font-medium">{holiday.holiday_name}</span>
                            <span className="text-amber-600">({holiday.date})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 bg-amber-100 rounded border border-amber-200">
                      <p className="text-sm text-amber-800">
                        <strong>Total additional unpaid days: {phForfeiture.forfeitedDays}</strong>
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        To avoid this, take unpaid leave only before <em>or</em> after the public holiday — not on both sides. Half-day unpaid leave does not trigger this rule.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Conflict Message: real overlapping leave only (never date-order errors) */}
            {conflictCheck.hasConflicts && !isDateOrderInvalid && !isSick && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-orange-800 mb-2">Leave Conflict Detected</h4>
                    <p className="text-sm text-orange-700 mb-3">
                      Your selected dates overlap with existing leave applications:
                    </p>
                    <div className="space-y-2">
                      {conflictCheck.conflicts.length > 0 ? (
                        conflictCheck.conflicts.map((conflict, index) => (
                          <div key={index} className="bg-white rounded border border-orange-200 p-3">
                            <div className="text-sm text-orange-700 flex items-center gap-1">
                              <span className="font-medium text-orange-900">{conflict.leave_type}</span>
                              <span>• {formatDate(conflict.start_date)} - {formatDate(conflict.end_date)}</span>
                              <span>• {formatLeaveDays(conflict.total_days)} days</span>
                            </div>
                          </div>
                        ))
                      ) : null}
                    </div>
                    <div className="mt-3 text-sm text-orange-700">
                      <strong>Action Required:</strong> Please select different dates to proceed with your leave application.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading state removed - conflict check now happens silently in background */}

            {/* Reason Section - Only for specific leave types and when no conflicts */}
            {fieldConfig.showReason && !conflictCheck.hasConflicts && !isDateOrderInvalid && (
              <div className="form-group">
                <label className="form-label">
                  <FileText className="inline w-4 h-4 mr-2" />
                  Reason for Leave
                  <span className="text-red-500 ml-1">*</span>
                </label>
                
                {isAnnual ? (
                  <div className="space-y-3">
                    <FormSelect
                      value={formData.leave_reason}
                      onChange={(newValue) => setFormData(prev => ({ ...prev, leave_reason: newValue }))}
                      options={[
                        { value: '', label: '🔍 Select a reason for your leave...' },
                        ...annualLeaveReasons.map((reason) => ({ value: reason, label: reason }))
                      ]}
                      theme="employee"
                      placeholder="🔍 Select a reason for your leave..."
                    />
                    
                    {/* Show textarea when "Other" is selected */}
                    {formData.leave_reason === 'Other' && (
                      <div className="mt-3">
                        <label className="form-label">
                          Please specify your reason: <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={formData.leave_reason_details || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, leave_reason_details: e.target.value }))}
                          rows={4}
                          className={`textarea${errors.leave_reason_details ? ' input-error' : ''}`}
                          placeholder="Please provide details about your leave reason..."
                        />
                        {errors.leave_reason_details && (
                          <p className="text-red-600 text-sm mt-2">{errors.leave_reason_details}</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : isWFH ? (
                  /* WFH Leave: Dropdown with WFH specific reasons */
                  <div className="space-y-3">
                    <FormSelect
                      value={formData.wfh_reason}
                      onChange={(newValue) => setFormData(prev => ({ ...prev, wfh_reason: newValue }))}
                      options={[
                        { value: '', label: '🏠 Select a reason' },
                        ...WFH_REASONS.map((reason) => ({ value: reason.value, label: reason.label }))
                      ]}
                      theme="employee"
                      placeholder="🏠 Select a reason"
                    />
                    
                    {/* Show text input when "Others" is selected */}
                    {formData.wfh_reason === 'others' && (
                      <div className="mt-3">
                        <label className="text-sm text-gray-600 mb-2 block">
                          Please specify your reason:
                        </label>
                        <input
                          type="text"
                          value={formData.wfh_other_reason || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, wfh_other_reason: e.target.value }))}
                          className="input"
                          placeholder="Please provide details about your WFH reason..."
                          required
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  /* Other leave types: Textarea for custom reasons */
                  <textarea
                    value={formData.leave_reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, leave_reason: e.target.value }))}
                    rows={4}
                    className="textarea"
                    placeholder="Please provide a brief reason for your leave application..."
                    required
                  />
                )}
                
                {errors.leave_reason && (
                  <p className="text-red-600 text-sm mt-2">{errors.leave_reason}</p>
                )}
              </div>
            )}

            {/* "Claim: Off in Lieu" Specific Fields (Accrual) */}
            {isOffInLieuClaim && (
              <div className="space-y-4">
                {/* 30-Day Claim Window Notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Claim Window: {OIL_CLAIM_WINDOW_DAYS} days</p>
                      <p className="text-xs mt-1">You must submit your OIL claim within {OIL_CLAIM_WINDOW_DAYS} days of the work date. OIL expires {OIL_EXPIRY_DAYS} days from the work date.</p>
                    </div>
                  </div>
                </div>
                
                {/* Multi-date OIL Selection */}
                <div className="form-group">
                  <label className="form-label">
                    <Calendar className="inline w-4 h-4 mr-2" />
                    Date(s) of Work Claimed for Off in Lieu
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  
                  {/* Date entries */}
                  <div className="space-y-3">
                    {formData.oil_dates.length === 0 ? (
                      // Initial state - show add button
                      <button
                        type="button"
                        onClick={addOILDate}
                        className="w-full py-3 px-4 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-400 text-gray-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
                      >
                        <Calendar className="w-4 h-4" />
                        Add work date
                      </button>
                    ) : (
                      formData.oil_dates.map((entry, index) => {
                        const dateValidation = validateOILDate(entry.date);
                        const claimWindowValidation = entry.date ? validateOILClaimWindow(entry.date) : null;
                        const isDuplicate = entry.date && isDateAlreadySelected(entry.date, index);
                        
                        return (
                          <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                            {/* Date header with remove button */}
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">
                                Date {index + 1}
                              </span>
                              {formData.oil_dates.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeOILDate(index)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                  title="Remove this date"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                            
                            {/* Date picker - restricted to 30-day claim window, up to yesterday (work must be completed) */}
                            <DatePickerField
                              theme="employee"
                              showIcon={false}
                              selected={entry.date ? parseLocalISOToDate(entry.date) : null}
                              onChange={(date) => {
                                const newDate = date ? toLocalISODate(date) : '';
                                updateOILDate(index, 'date', newDate);
                              }}
                              minDate={(() => {
                                // 30-day claim window - can only claim work done within last 30 days
                                const minClaimDate = new Date();
                                minClaimDate.setDate(minClaimDate.getDate() - OIL_CLAIM_WINDOW_DAYS);
                                return minClaimDate;
                              })()}
                              maxDate={(() => {
                                // Can only claim for completed work days - yesterday at the latest
                                const yesterday = new Date();
                                yesterday.setDate(yesterday.getDate() - 1);
                                return yesterday;
                              })()}
                              className="input rounded-full px-4 w-full"
                              placeholderText="Select work date"
                              portalId="datepicker-portal"
                              popperClassName="datepicker-popper-high-z"
                              popperPlacement="bottom-start"
                            />
                            
                            {/* Duplicate warning */}
                            {isDuplicate && (
                              <div className="mt-2 p-2 rounded text-sm bg-red-50 text-red-700 border border-red-200">
                                ✗ This date is already selected
                              </div>
                            )}
                            
                            {/* Date validation feedback - Day type check */}
                            {entry.date && !isDuplicate && (
                              <div className={`mt-2 p-2 rounded text-sm ${
                                dateValidation.valid 
                                  ? 'bg-green-50 text-green-700 border border-green-200' 
                                  : 'bg-red-50 text-red-700 border border-red-200'
                              }`}>
                                {dateValidation.valid ? '✓' : '✗'} {dateValidation.message}
                              </div>
                            )}
                            
                            {/* 30-Day Claim Window warning - only show if deadline is approaching or passed */}
                            {entry.date && !isDuplicate && dateValidation.valid && claimWindowValidation && (
                              // Only show warning if deadline is approaching (7 days or less) or invalid
                              (claimWindowValidation.daysRemaining <= 7 || !claimWindowValidation.isValid) && (
                                <div className={`mt-2 p-2 rounded text-sm ${
                                  claimWindowValidation.isValid 
                                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                    : 'bg-red-50 text-red-700 border border-red-200'
                                }`}>
                                  {claimWindowValidation.message}
                                </div>
                              )
                            )}
                            
                            {/* Off in Lieu Days selector for this date */}
                            {entry.date && dateValidation.valid && !isDuplicate && (() => {
                              const attendance = oilAttendanceByDate[entry.date];
                              const hoursCapped = shouldCapOilToHalfDay({
                                hours: attendance?.hours,
                                hasPair: attendance?.hasPair,
                                forceFullDay: dateValidation.forceFullDay,
                                restrict05: dateValidation.restrict05,
                              });
                              const oneDayDisabled = Boolean(dateValidation.restrict05 || hoursCapped);
                              const inLabel = attendance?.clockIn
                                ? formatTimeString12Hour(attendance.clockIn)
                                : null;
                              const outLabel = attendance?.clockOut
                                ? formatTimeString12Hour(attendance.clockOut)
                                : null;
                              return (
                              <div className="mt-3 space-y-2">
                                {attendance?.loading && (
                                  <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    Checking clock-in / clock-out…
                                  </p>
                                )}
                                {!attendance?.loading && (inLabel || outLabel) && (
                                  <div className="flex items-start gap-1.5 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                                    <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                                    <span>
                                      {inLabel && outLabel
                                        ? `${inLabel} – ${outLabel}`
                                        : inLabel
                                          ? `Clocked in ${inLabel}`
                                          : `Clocked out ${outLabel}`}
                                      {attendance?.hours != null && (
                                        <span className="text-gray-500">
                                          {' '}· {formatOilWorkedHours(attendance.hours)}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}
                                <label className="text-xs text-gray-500 mb-1 block">Off in Lieu Days</label>
                                <PillToggle
                                  theme="employee"
                                  variant="text"
                                  fullWidth
                                  ariaLabel="Off in lieu days"
                                  value={String(entry.days || (hoursCapped || dateValidation.restrict05 ? '0.5' : ''))}
                                  onChange={(id) => updateOILDate(index, 'days', Number(id))}
                                  options={[
                                    { id: '0.5', label: '0.5 Day', disabled: dateValidation.forceFullDay },
                                    { id: '1', label: '1 Day', disabled: oneDayDisabled },
                                  ]}
                                />
                                {dateValidation.forceFullDay && (
                                  <p className="text-xs text-blue-700 mt-1">
                                    Public holiday on Saturday requires 1.0 day OIL credit for a 5-day work week.
                                  </p>
                                )}
                                {dateValidation.restrict05 && !dateValidation.forceFullDay && (
                                  <p className="text-xs text-amber-700 mt-1">
                                    Saturday is a half work day for your schedule — maximum 0.5 day.
                                  </p>
                                )}
                                {hoursCapped && !dateValidation.restrict05 && (
                                  <p className="text-xs text-amber-700 mt-1">
                                    Work under {OIL_FULL_DAY_MIN_HOURS} hours on a rest day earns 0.5 day.
                                  </p>
                                )}
                              </div>
                              );
                            })()}
                          </div>
                        );
                      })
                    )}
                    
                    {/* Add another date button */}
                    {formData.oil_dates.length > 0 && formData.oil_dates.length < 5 && (
                      <button
                        type="button"
                        onClick={addOILDate}
                        className="w-full py-2 px-4 rounded-full border border-gray-300 hover:border-blue-400 text-gray-600 hover:text-blue-600 transition-all flex items-center justify-center gap-2 text-sm"
                      >
                        <Calendar className="w-3 h-3" />
                        Add another date
                      </button>
                    )}
                  </div>
                  
                  {errors.oil_dates && (
                    <p className="text-red-600 text-sm mt-1">{errors.oil_dates}</p>
                  )}
                </div>

                {/* Total Off in Lieu Days Summary */}
                {formData.oil_dates.length > 0 && totalOILDays > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-800">Total Off in Lieu Days</span>
                      <span className="text-lg font-bold text-blue-900">{totalOILDays} day{totalOILDays > 1 ? 's' : ''}</span>
                    </div>
                    {formData.oil_dates.length > 1 && (
                      <div className="mt-2 text-xs text-blue-600">
                        {formData.oil_dates.filter(e => e.date && e.days).map((e, i) => (
                          <span key={i}>
                            {i > 0 && ' + '}
                            {formatDate(e.date)}: {e.days}d
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Project before Work Description */}
                <div className="form-group">
                  <label className="form-label">
                    <Users className="inline w-4 h-4 mr-2" />
                    Project
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  
                  {/* Project Selection Display */}
                  {formData.project_code && formData.project_code !== 'Other' ? (
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-blue-900">{formData.project_name}</div>
                          <div className="text-sm text-blue-600 font-mono">{formData.project_code}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, project_code: '', project_name: '' }))}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : formData.project_code === 'Other' ? (
                    <div className="input flex items-center justify-between">
                      <div className="font-medium text-gray-900">Other</div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, project_code: '', project_name: '' }))}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : null}
                  
                  {/* Project Search Dropdown */}
                  {!formData.project_code && (
                    <div className="relative project-dropdown-container">
                      <input
                        type="text"
                        placeholder={projectsLoading ? 'Loading projects…' : 'Search projects by name or code...'}
                        className="input"
                        onChange={(e) => {
                          const searchTerm = e.target.value.toLowerCase();
                          // Filter projects based on search term
                          const filtered = activeProjects.filter(p => 
                            p.project_name?.toLowerCase().includes(searchTerm) ||
                            p.project_code?.toLowerCase().includes(searchTerm)
                          );
                          // Update filtered results for dropdown
                          setFilteredProjects(filtered);
                        }}
                        onFocus={() => setShowProjectDropdown(true)}
                        onBlur={() => {
                          // Small delay to allow click events on dropdown items
                          setTimeout(() => setShowProjectDropdown(false), 150);
                        }}
                      />
                      
                      {/* Project Dropdown */}
                      {showProjectDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {/* Other Option */}
                          <div
                            className="p-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, project_code: 'Other', project_name: 'Other' }));
                              setShowProjectDropdown(false);
                            }}
                          >
                            <div className="text-sm font-medium text-gray-900">Other</div>
                            <div className="text-xs text-gray-500 mt-0.5">Use for custom or external projects</div>
                          </div>
                          
                          {/* Project List */}
                          {filteredProjects.map((project) => (
                            <div
                              key={project.project_code}
                              className="p-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              onClick={() => {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  project_code: project.project_code,
                                  project_name: project.project_name
                                }));
                                setShowProjectDropdown(false);
                              }}
                            >
                              <div className="text-sm font-medium text-gray-900">{project.project_name}</div>
                              <div className="text-xs text-gray-500 font-mono mt-0.5">{project.project_code}</div>
                            </div>
                          ))}
                          
                          {filteredProjects.length === 0 && (
                            <div className="p-2.5 text-xs text-gray-500 text-center">
                              No projects found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {errors.project_code && (
                    <p className="text-red-600 text-sm mt-1">{errors.project_code}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <FileText className="inline w-4 h-4 mr-2" />
                    Work Description
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.work_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, work_description: e.target.value }))}
                    className="input"
                    placeholder="Please describe the work performed..."
                    required
                  />
                  {errors.work_description && (
                    <p className="text-red-600 text-sm mt-1">{errors.work_description}</p>
                  )}
                </div>

              </div>
            )}

            {/* "Off in Lieu" Usage Info - Shows when selecting OIL to use as time off */}
            {isOffInLieuUsage && (
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-teal-900">Using Off in Lieu Balance</h4>
                    <p className="text-sm text-teal-700 mt-1">
                      You are using your accrued Off in Lieu balance as time off.
                      Select the dates you wish to take off using the date fields above.
                    </p>
                    <p className="text-xs text-teal-600 mt-2">
                      💡 OIL is automatically used before Annual Leave when applying for annual leave.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* External Activity Specific Fields */}
            {isExternalActivity && (
              <div className="space-y-4">
                <div className="form-group">
                  <label className="form-label">
                    <Building className="inline w-4 h-4 mr-2" />
                    Activity Type
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <FormSelect
                    value={formData.activity_type}
                    onChange={(newValue) => setFormData(prev => ({ ...prev, activity_type: newValue }))}
                    options={[
                      { value: '', label: '🔍 Select an activity type...' },
                      ...activityTypes.map((type) => ({ value: type, label: type }))
                    ]}
                    theme="employee"
                    placeholder="🔍 Select an activity type..."
                  />
                  {errors.activity_type && (
                    <p className="text-red-600 text-sm mt-1">{errors.activity_type}</p>
                  )}
                </div>
                
                {/* Other Activity Type Text Input */}
                {formData.activity_type === 'Other' && (
                  <div className="form-group">
                    <label className="form-label">
                      <FileText className="inline w-4 h-4 mr-2" />
                      Specify Other Activity Type
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.other_activity_type || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, other_activity_type: e.target.value }))}
                      className="input"
                      placeholder="Please specify the activity type..."
                      required
                    />
                    {errors.other_activity_type && (
                      <p className="text-red-600 text-sm mt-1">{errors.other_activity_type}</p>
                    )}
                  </div>
                )}

                {/* Project selection with searchable dropdown */}
                <div className="form-group">
                  <label className="form-label">
                    <Users className="inline w-4 h-4 mr-2" />
                    Project
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  
                  {/* Project Selection Display */}
                  {formData.project_code && formData.project_code !== 'Other' ? (
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-blue-900">{formData.project_name}</div>
                          <div className="text-sm text-blue-600 font-mono">{formData.project_code}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, project_code: '', project_name: '' }))}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : formData.project_code === 'Other' ? (
                    <div className="input flex items-center justify-between">
                      <div className="font-medium text-gray-900">Other</div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, project_code: '', project_name: '' }))}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : null}
                  
                  {/* Project Search Dropdown */}
                  {!formData.project_code && (
                    <div className="relative project-dropdown-container">
                      <input
                        type="text"
                        placeholder={projectsLoading ? 'Loading projects…' : 'Search projects by name or code...'}
                        className="input"
                        onChange={(e) => {
                          const searchTerm = e.target.value.toLowerCase();
                          // Filter projects based on search term
                          const filtered = activeProjects.filter(p => 
                            p.project_name?.toLowerCase().includes(searchTerm) ||
                            p.project_code?.toLowerCase().includes(searchTerm)
                          );
                          // Update filtered results for dropdown
                          setFilteredProjects(filtered);
                        }}
                        onFocus={() => setShowProjectDropdown(true)}
                        onBlur={() => {
                          // Small delay to allow click events on dropdown items
                          setTimeout(() => setShowProjectDropdown(false), 150);
                        }}
                        required
                      />
                      
                      {/* Project Dropdown */}
                      {showProjectDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {/* Other Option */}
                          <div
                            className="p-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, project_code: 'Other', project_name: 'Other' }));
                              setShowProjectDropdown(false);
                            }}
                          >
                            <div className="text-sm font-medium text-gray-900">Other</div>
                            <div className="text-xs text-gray-500 mt-0.5">Use for custom or external projects</div>
                          </div>
                          
                          {/* Project List */}
                          {filteredProjects.map((project) => (
                            <div
                              key={project.project_code}
                              className="p-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              onClick={() => {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  project_code: project.project_code,
                                  project_name: project.project_name
                                }));
                                setShowProjectDropdown(false);
                              }}
                            >
                              <div className="text-sm font-medium text-gray-900">{project.project_name}</div>
                              <div className="text-xs text-gray-500 font-mono mt-0.5">{project.project_code}</div>
                            </div>
                          ))}
                          
                          {filteredProjects.length === 0 && (
                            <div className="p-2.5 text-xs text-gray-500 text-center">
                              No projects found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {errors.project_code && (
                    <p className="text-red-600 text-sm mt-1">{errors.project_code}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <MapPin className="inline w-4 h-4 mr-2" />
                    Activity Location
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.activity_location}
                    onChange={(e) => setFormData(prev => ({ ...prev, activity_location: e.target.value }))}
                    className="input"
                    required
                  />
                  <p className="text-gray-500 text-sm mt-1">Where this activity takes place</p>
                  {errors.activity_location && (
                    <p className="text-red-600 text-sm mt-1">{errors.activity_location}</p>
                  )}
                </div>


                <div className="form-group">
                  <label className="form-label">
                    <FileText className="inline w-4 h-4 mr-2" />
                    Purpose / Notes
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.business_justification}
                    onChange={(e) => setFormData(prev => ({ ...prev, business_justification: e.target.value }))}
                    className="input"
                    placeholder="Brief purpose or notes for this activity..."
                    required
                  />
                  {errors.business_justification && (
                    <p className="text-red-600 text-sm mt-1">{errors.business_justification}</p>
                  )}
                </div>
              </div>
            )}

            {/* Compassionate Leave Section — Relationship, Deceased Name, Death Certificate */}
            {isCompassionateLeave && (
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">🕊️</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-amber-800">Compassionate Leave</p>
                      <p className="text-xs text-amber-600 mt-1">
                        {formData.compassionate_sub_type === 'grandparent'
                          ? 'Paid bereavement leave for grandparents: 1 day per event. Lifetime cap: 4 grandparents.'
                          : formData.compassionate_sub_type === 'immediate_family'
                            ? 'Paid bereavement leave for immediate family (spouse, children, parents, siblings): 3 days per event.'
                            : 'Please select a type below. Death certificate required within 30 days.'
                        }
                        {formData.compassionate_sub_type && ' Death certificate required within 30 days.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sub-type Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <FormSelect
                    value={formData.compassionate_sub_type}
                    onChange={(newValue) => {
                      setFormData(prev => ({
                        ...prev,
                        compassionate_sub_type: newValue,
                        compassionate_relationship: '',
                      }));
                    }}
                    options={[
                      { value: '', label: 'Select type...' },
                      { value: 'immediate_family', label: 'Immediate Family (3 days per event)' },
                      { value: 'grandparent', label: 'Grandparent (1 day per event)' },
                    ]}
                    theme="employee"
                    placeholder="Select type..."
                  />
                  {errors.compassionate_sub_type && (
                    <p className="text-red-600 text-sm mt-1">{errors.compassionate_sub_type}</p>
                  )}
                </div>

                {/* Relationship to Deceased — only shown after sub-type is selected */}
                {formData.compassionate_sub_type && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relationship to Deceased <span className="text-red-500">*</span>
                  </label>
                  <FormSelect
                    value={formData.compassionate_relationship}
                    onChange={(newValue) => {
                      setFormData(prev => ({ ...prev, compassionate_relationship: newValue }));
                    }}
                    options={[
                      { value: '', label: 'Select relationship...' },
                      ...getAllCompassionateRelationships(
                        formData.compassionate_sub_type === 'grandparent' ? 'Grandparent' : 'Immediate Family'
                      ).map((opt) => ({ value: opt.value, label: opt.label }))
                    ]}
                    theme="employee"
                    placeholder="Select relationship..."
                  />
                  {errors.compassionate_relationship && (
                    <p className="text-red-600 text-sm mt-1">{errors.compassionate_relationship}</p>
                  )}
                  {formData.compassionate_relationship && (
                    <p className="text-xs text-gray-500 mt-1">
                      Entitlement: <span className="font-semibold">{getCompassionateEntitlementDays(formData.compassionate_relationship)} day(s)</span>
                    </p>
                  )}
                </div>
                )}

                {/* Deceased Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name of Deceased <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.deceased_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, deceased_name: e.target.value }))}
                    placeholder="Full name of the deceased"
                    className={`input text-sm ${errors.deceased_name ? 'input-error' : ''}`}
                  />
                  {errors.deceased_name && (
                    <p className="text-red-600 text-sm mt-1">{errors.deceased_name}</p>
                  )}
                </div>

                {/* Death Certificate Upload — Soft Header */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start gap-3 mb-3">
                    <FileText className="w-5 h-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Supporting Document — Death Certificate
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Optional at this time. You may upload the death certificate now or later from your leave history.
                        HR will require this document for verification within 30 days.
                      </p>
                    </div>
                  </div>
                  
                  {deathCertFileName ? (
                    <div className="flex items-center justify-between bg-white border border-green-200 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-700 truncate max-w-[200px]">{deathCertFileName}</span>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          if (deathCertDocId) {
                            try {
                              await staffAPI.deleteDeathCertificate(deathCertDocId);
                            } catch (err) {
                              console.error('Failed to delete death cert:', err);
                            }
                          }
                          setDeathCertDocId(null);
                          setDeathCertFileName(null);
                        }}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        ref={deathCertInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setDeathCertUploading(true);
                          try {
                            const res = await staffAPI.uploadDeathCertificate(file);
                            setDeathCertDocId(res.data.document_approval_id);
                            setDeathCertFileName(file.name);
                            toast.success('Death certificate uploaded');
                          } catch (err) {
                            const msg = err?.response?.data?.error || 'Upload failed';
                            toast.error(msg);
                          } finally {
                            setDeathCertUploading(false);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => deathCertInputRef.current?.click()}
                        disabled={deathCertUploading}
                        className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                      >
                        {deathCertUploading ? (
                          <>
                            <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload Death Certificate
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* On Course Section — Info Banner + Course Details */}
            {isOnCourse && (
              <div className="space-y-4">
                {/* Info Banner */}
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">🎓</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-violet-800">On Course (Auto-Approved)</p>
                      <p className="text-xs text-violet-600 mt-1">
                        Use this when attending a company-sanctioned training, course, or academic programme.
                        This is auto-approved and does not deduct from your leave balance.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Course / Training Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course / Training Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.course_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, course_name: e.target.value }))}
                    placeholder="e.g. Advanced Project Management, WSQ Workplace Safety"
                    className={`input text-sm ${errors.course_name ? 'input-error' : ''}`}
                  />
                  {errors.course_name && (
                    <p className="text-red-600 text-sm mt-1">{errors.course_name}</p>
                  )}
                </div>

                {/* Course Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.course_location}
                    onChange={(e) => setFormData(prev => ({ ...prev, course_location: e.target.value }))}
                    placeholder="e.g. Online, NUS Business School, Singapore Expo Hall 5"
                    className={`input text-sm ${errors.course_location ? 'input-error' : ''}`}
                  />
                  {errors.course_location && (
                    <p className="text-red-600 text-sm mt-1">{errors.course_location}</p>
                  )}
                </div>
              </div>
            )}
            </>
            )}

          </form>
      </Modal>
    </>
  );
};

export default SmartLeaveApplicationModal; 